<template>
  <div class="content-wrapper">
    <div class="appliances-page">
      <!-- MOTD Modal Component -->
      <MotdModal :route="$route.path" />

      <div class="title-bar">
        <span style="flex: 1">Appliance Tracking</span>
        <button class="back-button" @click="goBack">
          <i class="fas fa-arrow-left"></i>
        </button>
        <button
          v-if="!isMobile"
          @click="closeAppliances"
          class="home"
          title="Close"
        >
          <i class="fas fa-close"></i>
        </button>
        <button class="home" title="Home" @click="$router.push('/mhome')">
          <i class="fas fa-home"></i>
        </button>
      </div>

      <!-- Add appliance coverage progress bar -->
      <div class="appliance-coverage-bar" v-if="coverageStats">
        <div class="coverage-label">
          {{ coverageStats.withAppliances }} of
          {{ coverageStats.total }} properties ({{ coverageStats.percentage }}%)
        </div>
        <div class="progress-bar">
          <div
            class="progress-bar-fill"
            :style="{ width: coverageStats.percentage + '%' }"
          ></div>
        </div>
      </div>

      <!-- In Progress Summary Section -->
      <div
        v-if="loadingInProgress || inProgressAppliances.length > 0"
        class="in-progress-section"
      >
        <div class="in-progress-header" @click="toggleInProgress">
          <h4>
            <i class="fas fa-cog"></i>
            Service/Replace Requests
            <span
              v-if="inProgressAppliances.length > 0"
              class="in-progress-count"
            >
              {{ inProgressAppliances.length }}
            </span>
          </h4>
          <i
            class="fas"
            :class="showInProgress ? 'fa-chevron-up' : 'fa-chevron-down'"
          ></i>
        </div>

        <div v-if="showInProgress" class="in-progress-content">
          <div v-if="loadingInProgress" class="loading-indicator">
            <i class="fas fa-spinner fa-spin"></i> Loading requests...
          </div>
          <div
            v-else-if="inProgressAppliances.length === 0"
            class="no-requests"
          >
            No active service or replace requests
          </div>
          <div v-else class="in-progress-list">
            <div
              v-for="appliance in inProgressAppliances"
              :key="appliance.id"
              class="in-progress-item"
              @click="openApplianceDialog(appliance)"
            >
              <div class="item-header">
                <div class="appliance-info">
                  <strong>{{ appliance.propertyName }}</strong>
                </div>
                <div class="slider-container" @click.stop>
                  <div
                    class="mark-received-slider"
                    @click="markAsReceived(appliance.id)"
                    title="Mark as received"
                  >
                    <div class="slider-track">
                      <div class="slider-thumb"></div>
                    </div>
                  </div>
                  <div class="completed-label">completed?</div>
                </div>
              </div>
              <div class="item-details">
                <div class="property-name">
                  {{ appliance.make }} {{ appliance.model }}
                  <span class="request-type">
                    ({{ formatServiceOrReplace(appliance.serviceOrReplace) }})
                  </span>
                </div>
                <div class="request-info">
                  Requested: {{ formatDate(appliance.requestedOn) }}
                  <span>
                    | Due by:
                    {{ appliance.dueBy ? formatDate(appliance.dueBy) : '' }}
                  </span>
                </div>
                <div v-if="appliance.requestedByName" class="requested-by">
                  By: {{ appliance.requestedByName }}
                </div>
                <div v-if="appliance.mostRecentNote" class="most-recent-note">
                  {{ appliance.mostRecentNote }}
                </div>
              </div>
              <!-- Note icon in lower right corner -->
              <div
                class="in-progress-note-icon"
                @click.stop="openInProgressNoteDialog(appliance)"
                title="Add note"
              >
                <i class="fas fa-sticky-note"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="content">
        <!-- Search view -->
        <div
          v-if="currentView === 'properties'"
          :class="{ 'content-blurred': applianceDialog }"
        >
          <div v-if="!selectedProperty">
            Search by address or appliance type.<br />Tap a search result to
            display the appliances.
          </div>
          <div class="search-bar">
            <input
              style="margin-left: -10px"
              ref="searchInput"
              v-model="searchQuery"
              @input="filterProperties"
              @focus="handleSearchFocus"
              placeholder="Search address or type"
            />
            <button class="clear-search" @click="clearSearch">
              <i class="fas fa-backspace"></i>
            </button>
          </div>

          <!-- Examples section showing address and appliance type searches -->
          <div v-if="!selectedProperty && !filteredProperties.length">
            <div class="example">
              example: <br /><br />type
              <strong style="color: #907793">121 Mad 15 </strong> to find 121
              Madison Apt 15
            </div>
            <div class="example">
              or type
              <strong style="color: #907793">refrigerator</strong> to find all
              properties with refrigerators
            </div>
            <div style="margin-bottom: 15px" class="example-indent"></div>

            <!-- Empty state with home icon -->
            <div class="empty-state" v-if="!searchQuery">
              <i class="fas fa-home empty-icon"></i>

              <!-- Recent Appliance Photos Carousel -->
              <div class="recent-photos-section">
                <div class="section-title-row">
                  <h3>Recent Appliance Photos</h3>
                  <button
                    class="refresh-button"
                    @click="fetchRecentPhotos(true)"
                    title="Refresh photos"
                  >
                    <i class="fas fa-refresh"></i>
                  </button>
                </div>
                <div class="photo-carousel">
                  <button
                    class="carousel-arrow left"
                    @click="prevCarouselPhoto"
                  >
                    <i class="fas fa-chevron-left"></i>
                  </button>
                  <div v-if="isLoadingRecentPhotos" class="loading-photos">
                    <i
                      class="fas fa-spinner fa-spin"
                      style="margin-right: 8px"
                    ></i>
                    Loading photos...
                  </div>
                  <div
                    v-else-if="recentPhotos.length === 0"
                    class="no-recent-photos"
                  >
                    No recent appliance photos
                  </div>
                  <div v-else class="carousel-inner" ref="carouselInner">
                    <div
                      v-for="photo in recentPhotos"
                      :key="photo.id"
                      class="photo-item"
                      @click="openPhotoFromCarousel(photo)"
                    >
                      <img
                        :src="photo.photoUrl"
                        :alt="photo.caption || 'Appliance photo'"
                      />
                      <div
                        class="photo-caption"
                        :title="photo.caption || 'No caption'"
                      >
                        {{ photo.caption || 'No caption' }}
                      </div>
                      <div class="photo-appliance" :title="photo.applianceType">
                        {{ photo.applianceType }}
                      </div>
                      <div class="photo-address" :title="photo.propertyAddress">
                        {{ formatAddress(photo.propertyAddress) }}
                      </div>
                    </div>
                  </div>
                  <button
                    class="carousel-arrow right"
                    @click="nextCarouselPhoto"
                  >
                    <i class="fas fa-chevron-right"></i>
                  </button>
                </div>

                <!-- Add carousel navigation dots -->
                <div v-if="recentPhotos.length > 0" class="carousel-controls">
                  <div class="carousel-dots">
                    <div
                      v-for="(photo, index) in recentPhotos"
                      :key="'dot-' + photo.id"
                      :class="{ active: index === currentCarouselIndex }"
                      class="carousel-dot"
                      @click="goToCarouselItem(index)"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style="min-height: 50vh" v-if="!selectedProperty">
            <div v-show="filteredProperties.length" class="match-label">
              Matches:
            </div>
            <div v-if="loading" class="loading-indicator">
              <i class="fas fa-spinner fa-spin"></i> Loading appliance data...
            </div>
            <ul v-else-if="filteredProperties.length">
              <li
                v-for="property in filteredProperties.slice(0, 8)"
                :key="property.id"
                @click="selectProperty(property)"
              >
                <div class="property-info">
                  <span
                    v-html="
                      highlightMatch(
                        formatAddress(property.propertyAddress),
                        searchQuery,
                      )
                    "
                  ></span>

                  <!-- Show appliance type matches when searching by type -->
                  <div v-if="isApplianceTypeSearch()" class="type-matches">
                    <div
                      v-for="(app, index) in getMatchingAppliances(property)"
                      :key="index"
                      class="type-match"
                    >
                      <i class="fas fa-check"></i>
                      <span
                        v-html="
                          highlightMatch(getApplianceTypeName(app), searchQuery)
                        "
                      ></span>
                    </div>
                  </div>
                </div>

                <span v-if="property.applianceCount > 0" class="badge">
                  {{ property.applianceCount }}
                </span>
              </li>
            </ul>
            <div
              v-else-if="searchQuery && !filteredProperties.length"
              class="no-results"
            >
              No properties found matching "{{ searchQuery }}"
            </div>
          </div>

          <!-- Property appliances list -->
          <div v-if="selectedProperty" class="property-details">
            <div class="back-button-2" @click="backToProperties">
              <i class="fas fa-arrow-left"></i> Back to Search
            </div>
            <h3>{{ formatAddress(selectedProperty.propertyAddress) }}</h3>

            <button class="add-button" @click="openApplianceDialog()">
              <i class="fas fa-plus"></i> Add Appliance
            </button>

            <div v-if="appliances.length === 0" class="no-appliances">
              No appliances found for this property.
            </div>

            <!-- Added scrollable wrapper around the appliances list -->
            <div class="appliances-list-wrapper">
              <ul v-if="appliances.length > 0" class="appliances-list">
                <li
                  v-for="appliance in sortedAppliances"
                  :key="appliance.id"
                  @click="viewApplianceDetails(appliance)"
                >
                  <div class="appliance-item">
                    <div class="appliance-header-row">
                      <div class="appliance-name">
                        {{ appliance.make }} {{ appliance.model }}
                      </div>
                      <div class="appliance-indicators">
                        <i
                          v-if="appliance.docId"
                          class="fas fa-file-alt document-indicator"
                          title="Has linked document"
                          @click.stop="viewDocument(appliance.docId)"
                        ></i>
                        <i
                          v-if="appliance.photoCount > 0"
                          class="fas fa-camera"
                          title="Has photos"
                        ></i>
                        <i
                          v-if="appliance.noteCount > 0"
                          class="fas fa-sticky-note"
                          title="Has notes"
                        ></i>
                      </div>
                    </div>
                    <div class="appliance-serial">{{ appliance.serialNo }}</div>
                    <div class="appliance-type">
                      {{
                        appliance.applianceType === 'Other'
                          ? appliance.customType
                          : appliance.applianceType
                      }}
                      <span
                        v-if="appliance.gasOrElectric === 1"
                        class="type-attribute"
                        >(gas)</span
                      >
                      <span
                        v-else-if="appliance.gasOrElectric === 2"
                        class="type-attribute"
                        >(electric)</span
                      >
                      <span
                        v-if="hasDimensionData(appliance)"
                        class="dimensions"
                      >
                        {{
                          formatDimensions(
                            appliance.width,
                            appliance.height,
                            appliance.depth,
                          )
                        }}
                      </span>
                    </div>
                    <div class="appliance-condition">
                      <span>Condition: </span>
                      <div class="condition-rating">
                        <span
                          v-for="n in 10"
                          :key="n"
                          :class="{
                            filled: n <= appliance.appliance_condition,
                          }"
                          class="rating-dot"
                        ></span>
                      </div>
                    </div>
                    <div class="appliance-date">
                      <span>
                        Installed: {{ formatDate(appliance.installDate) }}
                        <span v-if="appliance.installDate" class="age-display">
                          ({{ calculateApplianceAge(appliance.installDate) }})
                        </span>
                      </span>
                      <span>
                        Remove date: {{ formatDate(appliance.removeDate) }}
                      </span>
                    </div>
                  </div>
                  <div class="appliance-actions"></div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Appliance details view -->
        <div
          v-else-if="currentView === 'details'"
          class="details-view"
          :class="{ 'content-blurred': applianceDialog }"
        >
          <div class="fixed-header">
            <div class="back-button-2" @click="backToAppliances">
              <i class="fas fa-arrow-left"></i> Back to Appliances
            </div>

            <div v-if="selectedAppliance" class="appliance-header">
              <div class="appliance-header-row">
                <h3>
                  {{ selectedAppliance.make }} {{ selectedAppliance.model }}
                </h3>
                <div class="action-buttons">
                  <button
                    class="link-button"
                    :class="{ active: selectedAppliance.docId }"
                    @click="toggleDocumentLink"
                    title="Link to document"
                  >
                    <i class="fas fa-paperclip"></i>
                  </button>
                  <button class="edit-button" @click="toggleEditMode">
                    <i
                      class="fas"
                      :class="isEditing ? 'fa-save' : 'fa-pencil-alt'"
                    ></i>
                  </button>
                  <button
                    class="delete-button"
                    @click="confirmDeleteAppliance(selectedAppliance)"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="details-form-container">
            <div v-if="selectedAppliance">
              <div class="details-form">
                <div class="form-group">
                  <label>Make:</label>
                  <input
                    v-model="selectedAppliance.make"
                    :disabled="!isEditing"
                    :class="{ editable: isEditing, readonly: !isEditing }"
                    @blur="updateAppliance('make')"
                  />
                </div>
                <div class="form-group">
                  <label>Type:</label>
                  <div class="appliance-type-selector" v-if="isEditing">
                    <select
                      v-model="selectedAppliance.applianceType"
                      @change="handleTypeChange()"
                      @blur="updateAppliance('applianceType')"
                    >
                      <option value="Refrigerator">Refrigerator</option>
                      <option value="Range/Stove">Stove</option>
                      <option value="Oven">Oven</option>
                      <option value="Dishwasher">Dishwasher</option>
                      <option value="Freezer">Freezer</option>
                      <option value="Microwave">Microwave</option>
                      <option value="Washer">Washer</option>
                      <option value="Dryer">Dryer</option>
                      <option value="Air Conditioner">Air Conditioner</option>
                      <option value="Heater">Heater</option>
                      <option value="Other">Other</option>
                    </select>
                    <input
                      v-if="selectedAppliance.applianceType === 'Other'"
                      v-model="selectedAppliance.customType"
                      placeholder="Specify type..."
                      @blur="updateCustomType"
                    />
                  </div>
                  <div v-else class="readonly appliance-type-display">
                    {{
                      selectedAppliance.applianceType === 'Other'
                        ? selectedAppliance.customType
                        : selectedAppliance.applianceType
                    }}
                  </div>
                </div>
                <div class="form-group">
                  <label>Model:</label>
                  <input
                    v-model="selectedAppliance.model"
                    :disabled="!isEditing"
                    :class="{ editable: isEditing, readonly: !isEditing }"
                    @blur="updateAppliance('model')"
                  />
                </div>
                <div class="form-group">
                  <label>Serial Number:</label>
                  <input
                    v-model="selectedAppliance.serialNo"
                    :disabled="!isEditing"
                    :class="{ editable: isEditing, readonly: !isEditing }"
                    @blur="updateAppliance('serialNo')"
                  />
                </div>
                <div class="form-group">
                  <label>Gas or Electric:</label>
                  <div v-if="isEditing" class="appliance-type-selector">
                    <select
                      v-model="selectedAppliance.gasOrElectric"
                      @change="updateAppliance('gasOrElectric')"
                      :class="{ editable: isEditing }"
                    >
                      <option :value="null">Not Applicable</option>
                      <option :value="1">Gas</option>
                      <option :value="2">Electric</option>
                    </select>
                  </div>
                  <div v-else class="readonly appliance-type-display">
                    {{ formatGasOrElectric(selectedAppliance.gasOrElectric) }}
                  </div>
                </div>
                <div class="form-group dimensions-container">
                  <label>Dimensions (inches):</label>
                  <div class="dimensions-row">
                    <div class="dimension-input">
                      <label>Width:</label>
                      <input
                        v-model.number="selectedAppliance.width"
                        :disabled="!isEditing"
                        :class="{ editable: isEditing, readonly: !isEditing }"
                        type="number"
                        step="0.1"
                        min="0"
                        @blur="updateAppliance('width')"
                      />
                    </div>
                    <div class="dimension-input">
                      <label>Height:</label>
                      <input
                        v-model.number="selectedAppliance.height"
                        :disabled="!isEditing"
                        :class="{ editable: isEditing, readonly: !isEditing }"
                        type="number"
                        step="0.1"
                        min="0"
                        @blur="updateAppliance('height')"
                      />
                    </div>
                    <div class="dimension-input">
                      <label>Depth:</label>
                      <input
                        v-model.number="selectedAppliance.depth"
                        :disabled="!isEditing"
                        :class="{ editable: isEditing, readonly: !isEditing }"
                        type="number"
                        step="0.1"
                        min="0"
                        @blur="updateAppliance('depth')"
                      />
                    </div>
                  </div>
                </div>
                <div class="date-pair">
                  <div class="form-group">
                    <label>Install Date:</label>
                    <input
                      v-if="isEditing"
                      type="date"
                      v-model="selectedAppliance.installDateISO"
                      :class="{ editable: isEditing }"
                      @blur="commitSelectedDate('installDate')"
                    />
                    <div v-else class="readonly">
                      {{ formatDate(selectedAppliance.installDate) }}
                    </div>
                  </div>
                  <div class="form-group">
                    <label>Remove date:</label>
                    <input
                      v-if="isEditing"
                      type="date"
                      v-model="selectedAppliance.removeDateISO"
                      :class="{ editable: isEditing }"
                      @blur="commitSelectedDate('removeDate')"
                    />
                    <div v-else class="readonly">
                      {{ formatDate(selectedAppliance.removeDate) }}
                    </div>
                  </div>
                </div>
                <p v-if="dateFieldError" class="date-field-error">
                  {{ dateFieldError }}
                </p>
                <div class="form-group">
                  <label>Last Service Date:</label>
                  <input
                    v-if="isEditing"
                    type="date"
                    v-model="selectedAppliance.lastServiceDateISO"
                    :class="{ editable: isEditing }"
                    @change="handleDateChange('lastServiceDate')"
                  />
                  <div v-else class="readonly">
                    {{ formatDate(selectedAppliance.lastServiceDate) }}
                  </div>
                </div>
                <div class="form-group">
                  <label>Replace After (years):</label>
                  <input
                    v-model="selectedAppliance.replaceAfter"
                    type="number"
                    :disabled="!isEditing"
                    :class="{ editable: isEditing, readonly: !isEditing }"
                    @blur="updateAppliance('replaceAfter')"
                  />
                </div>
                <div class="form-group">
                  <label>Condition:</label>
                  <div class="condition-rating-edit">
                    <span
                      v-for="n in 10"
                      :key="n"
                      :class="{
                        filled: n <= selectedAppliance.appliance_condition,
                        clickable: isEditing,
                      }"
                      class="rating-dot"
                      @click="isEditing && setCondition(n)"
                    ></span>
                  </div>
                </div>

                <!-- Service/Replace Request Section -->
                <div class="section-header">
                  <h4>Service/Replace Request</h4>
                </div>
                <div class="form-group">
                  <label>Request Type:</label>
                  <select
                    v-if="isEditing"
                    v-model="selectedAppliance.serviceOrReplace"
                    class="request-type-select"
                    :class="{ editable: isEditing }"
                    @change="updateAppliance('serviceOrReplace')"
                  >
                    <option :value="null">None</option>
                    <option :value="1">Service</option>
                    <option :value="2">Replace</option>
                  </select>
                  <div v-else class="readonly">
                    {{
                      formatServiceOrReplace(selectedAppliance.serviceOrReplace)
                    }}
                  </div>
                </div>
                <div class="form-group">
                  <label>Requested On:</label>
                  <input
                    v-if="isEditing"
                    type="date"
                    v-model="selectedAppliance.requestedOnISO"
                    :class="{ editable: isEditing }"
                    @change="handleDateChange('requestedOn')"
                  />
                  <div v-else class="readonly">
                    {{
                      selectedAppliance.requestedOn
                        ? new Date(
                            selectedAppliance.requestedOn,
                          ).toLocaleDateString()
                        : 'N/A'
                    }}
                  </div>
                </div>
                <div class="form-group">
                  <label>Due By:</label>
                  <input
                    v-if="isEditing"
                    type="date"
                    v-model="selectedAppliance.dueByISO"
                    :class="{ editable: isEditing }"
                    @change="handleDateChange('dueBy')"
                  />
                  <div v-else class="readonly">
                    {{
                      selectedAppliance.dueBy
                        ? new Date(selectedAppliance.dueBy).toLocaleDateString()
                        : 'N/A'
                    }}
                  </div>
                </div>
                <div class="form-group">
                  <label>Received:</label>
                  <input
                    v-if="isEditing"
                    type="date"
                    v-model="selectedAppliance.receivedISO"
                    :class="{ editable: isEditing }"
                    @change="handleDateChange('received')"
                  />
                  <div v-else class="readonly">
                    {{
                      selectedAppliance.received
                        ? new Date(
                            selectedAppliance.received,
                          ).toLocaleDateString()
                        : 'N/A'
                    }}
                  </div>
                </div>

                <!-- Photos Section -->
                <div class="section-header">
                  <h4>Photos</h4>
                  <button
                    v-if="isEditing"
                    class="add-small-button"
                    @click="openPhotoUploadDialog"
                  >
                    <i class="fas fa-plus"></i>
                  </button>
                </div>

                <div class="photo-carousel" v-if="photos.length">
                  <button
                    v-if="photos.length > 4"
                    class="carousel-arrow left"
                    @click="scrollCarousel('left')"
                  >
                    <i class="fas fa-chevron-left"></i>
                  </button>
                  <div class="carousel-inner" ref="carouselInner">
                    <div
                      v-for="photo in photos"
                      :key="photo.id"
                      class="photo-item"
                      @click="openPhotoDialog(photo)"
                    >
                      <img :src="photo.photoUrl" :alt="photo.caption" />
                      <div class="photo-caption">
                        {{ photo.caption || 'No caption' }}
                      </div>
                      <button
                        v-if="isEditing"
                        class="delete-photo-button"
                        @click.stop="confirmDeletePhoto(photo)"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                  <button
                    v-if="photos.length > 4"
                    class="carousel-arrow right"
                    @click="scrollCarousel('right')"
                  >
                    <i class="fas fa-chevron-right"></i>
                  </button>
                </div>
                <div v-else class="no-photos">No photos available</div>

                <!-- Notes Section -->
                <div class="section-header">
                  <h4>Notes</h4>
                </div>

                <div v-if="isEditing" class="add-note">
                  <textarea
                    v-model="newNote"
                    placeholder="Add a new note..."
                    rows="3"
                  ></textarea>
                  <div class="note-actions">
                    <button
                      class="link-document-button"
                      :class="{ active: noteDocId !== null }"
                      @click="toggleNoteDocumentLink"
                      title="Link to document"
                    >
                      <i class="fas fa-paperclip"></i>
                    </button>
                    <button
                      class="save-note-button"
                      :disabled="!newNote.trim()"
                      @click="addNote"
                    >
                      <i class="fas fa-save"></i>
                    </button>
                  </div>
                </div>

                <div class="notes-list" v-if="notes.length">
                  <div v-for="note in notes" :key="note.id" class="note-item">
                    <div
                      v-if="editingNoteId === note.id"
                      class="note-edit-area"
                    >
                      <textarea v-model="editedNoteText" rows="3"></textarea>
                      <div class="note-actions">
                        <button
                          class="link-document-button"
                          :class="{ active: editedNoteDocId !== null }"
                          @click="toggleEditedNoteDocumentLink"
                          title="Link to document"
                        >
                          <i class="fas fa-paperclip"></i>
                        </button>
                        <button
                          class="save-note-button"
                          :disabled="!editedNoteText.trim()"
                          @click="updateNote(note)"
                        >
                          <i class="fas fa-save"></i>
                        </button>
                        <button
                          class="cancel-note-button"
                          @click="cancelEditNote"
                          title="Cancel"
                        >
                          <i class="fas fa-times"></i>
                        </button>
                      </div>
                    </div>
                    <div v-else>
                      <div
                        class="note-content"
                        v-html="markedNote(note.note)"
                      ></div>
                      <div class="note-meta">
                        <div>
                          {{ formatDateTime(note.modifiedOn) }} by
                          {{ note.modifiedByName }}
                          <i
                            v-if="note.docId"
                            class="fas fa-paperclip note-document-icon"
                            title="View linked document"
                            @click="viewNoteDocument(note.docId)"
                          ></i>
                        </div>
                        <div v-if="isEditing" class="note-actions-small">
                          <button
                            class="edit-note-button"
                            @click="editNote(note)"
                            title="Edit note"
                          >
                            <i class="fas fa-pencil-alt"></i>
                          </button>
                          <button
                            class="delete-note-button"
                            @click="confirmDeleteNote(note)"
                            title="Delete note"
                          >
                            <i class="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="modified-date" v-if="selectedAppliance.modifiedOn">
                  Updated: {{ formatDate(selectedAppliance.modifiedOn) }} by
                  {{ selectedAppliance.modifiedByName }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Add Appliance Dialog - Positioned to overlay the content -->
  <div v-if="applianceDialog" class="content-overlay-dialog">
    <div class="dialog-content appliance-dialog">
      <div class="dialog-header">
        {{ isEditMode ? 'Edit Appliance' : 'Add New Appliance' }}
      </div>
      <div class="dialog-body">
        <div class="form-group">
          <label>Make:</label>
          <input v-model="editedAppliance.make" required />
        </div>
        <div class="form-group">
          <label>Type:</label>
          <div class="appliance-type-selector">
            <select
              v-model="editedAppliance.applianceType"
              @change="handleEditedTypeChange()"
            >
              <option value="Refrigerator">Refrigerator</option>
              <option value="Stove">Stove</option>
              <option value="Oven">Oven</option>
              <option value="Dishwasher">Dishwasher</option>
              <option value="Freezer">Freezer</option>
              <option value="Microwave">Microwave</option>
              <option value="Washer">Washer</option>
              <option value="Dryer">Dryer</option>
              <option value="Air Conditioner">Air Conditioner</option>
              <option value="Heater">Heater</option>
              <option value="Other">Other</option>
            </select>
            <input
              v-if="editedAppliance.applianceType === 'Other'"
              v-model="editedAppliance.customType"
              placeholder="Specify type..."
              class="custom-type-input"
            />
          </div>
        </div>
        <div class="form-group">
          <label>Model:</label>
          <input v-model="editedAppliance.model" required />
        </div>
        <div class="form-group">
          <label>Serial Number:</label>
          <input v-model="editedAppliance.serialNo" />
        </div>
        <div class="form-group">
          <label>Gas or Electric:</label>
          <div class="appliance-type-selector">
            <select v-model="editedAppliance.gasOrElectric">
              <option :value="null">Not Applicable</option>
              <option :value="1">Gas</option>
              <option :value="2">Electric</option>
            </select>
          </div>
        </div>
        <div class="form-group dimensions-container">
          <label>Dimensions (inches):</label>
          <div class="dimensions-row">
            <div class="dimension-input">
              <label>Width:</label>
              <input
                v-model.number="editedAppliance.width"
                type="number"
                step="0.1"
                min="0"
              />
            </div>
            <div class="dimension-input">
              <label>Height:</label>
              <input
                v-model.number="editedAppliance.height"
                type="number"
                step="0.1"
                min="0"
              />
            </div>
            <div class="dimension-input">
              <label>Depth:</label>
              <input
                v-model.number="editedAppliance.depth"
                type="number"
                step="0.1"
                min="0"
              />
            </div>
          </div>
        </div>
        <div class="date-pair">
          <div class="form-group">
            <label>Install Date:</label>
            <input
              type="date"
              v-model="editedAppliance.installDateISO"
              @blur="commitEditedDate('installDate')"
            />
          </div>
          <div class="form-group">
            <label>Remove date:</label>
            <input
              type="date"
              v-model="editedAppliance.removeDateISO"
              @blur="commitEditedDate('removeDate')"
            />
          </div>
        </div>
        <p v-if="dateDialogError" class="date-field-error">
          {{ dateDialogError }}
        </p>
        <div class="form-group">
          <label>Last Service Date:</label>
          <input
            type="date"
            v-model="editedAppliance.lastServiceDateISO"
            @change="handleEditedDateChange('lastServiceDate')"
          />
        </div>
        <div class="form-group">
          <label>Replace After (years):</label>
          <input
            type="number"
            v-model.number="editedAppliance.replaceAfter"
            min="0"
          />
        </div>
        <div class="form-group">
          <label>Condition:</label>
          <div class="condition-rating-edit">
            <span
              v-for="n in 10"
              :key="n"
              :class="{
                filled: n <= editedAppliance.appliance_condition,
              }"
              class="rating-dot clickable"
              @click="editedAppliance.appliance_condition = n"
            ></span>
          </div>
        </div>

        <!-- Service/Replace Request Section -->
        <div class="form-section">
          <h5>Service/Replace Request</h5>
          <div class="form-group">
            <label>Request Type:</label>
            <select
              v-model="editedAppliance.serviceOrReplace"
              class="request-type-select"
            >
              <option :value="null">None</option>
              <option :value="1">Service</option>
              <option :value="2">Replace</option>
            </select>
          </div>
          <div class="form-group">
            <label>Requested On:</label>
            <input
              type="date"
              v-model="editedAppliance.requestedOnISO"
              @change="handleEditedDateChange('requestedOn')"
            />
          </div>
          <!-- Requested By field removed - will be set by server using user token -->
          <div class="form-group">
            <label>Due By:</label>
            <input
              type="date"
              v-model="editedAppliance.dueByISO"
              @change="handleEditedDateChange('dueBy')"
            />
          </div>
          <div class="form-group">
            <label>Received:</label>
            <input
              type="date"
              v-model="editedAppliance.receivedISO"
              @change="handleEditedDateChange('received')"
            />
          </div>
        </div>
      </div>
      <div class="dialog-actions">
        <button @click="applianceDialog = false">Cancel</button>
        <button class="confirm-button" @click="saveAppliance">Save</button>
      </div>
    </div>
  </div>

  <!-- Photo Upload Dialog -->
  <div v-if="photoDialog" class="dialog-overlay" @click="photoDialog = false">
    <div class="dialog-content" @click.stop>
      <div class="dialog-header">Upload Photo</div>
      <div class="dialog-body">
        <div class="upload-instructions">
          Select an image from your device to upload:
        </div>
        <div class="upload-options">
          <label class="upload-option">
            <input
              type="file"
              accept="image/*"
              @change="handlePhotoSelect"
              class="hidden-input"
              multiple
            />
            <div class="option-button">
              <i class="fas fa-images"></i>
              <div>Gallery</div>
            </div>
          </label>
          <label class="upload-option">
            <input
              type="file"
              accept="image/*"
              capture="environment"
              @change="handlePhotoSelect"
              class="hidden-input"
              multiple
            />
            <div class="option-button">
              <i class="fas fa-camera"></i>
              <div>Camera</div>
            </div>
          </label>
        </div>
        <div
          class="preview-container"
          v-if="photoFiles && photoFiles.length > 0"
        >
          <div v-if="photoFiles.length === 1">
            <img :src="photoPreviewUrl" class="photo-preview" />
          </div>
          <div v-else class="multi-preview-container">
            <div
              v-for="(preview, index) in photoPreviewUrls"
              :key="index"
              class="multi-preview-item"
            >
              <img :src="preview" class="photo-preview-thumbnail" />
            </div>
          </div>
          <div class="photo-count" v-if="photoFiles.length > 1">
            {{ photoFiles.length }} photos selected
          </div>
        </div>
        <input
          v-model="photoCaption"
          placeholder="Caption (optional)"
          class="caption-input"
        />
      </div>
      <div class="dialog-actions">
        <button @click="photoDialog = false">Cancel</button>
        <button
          class="confirm-button"
          :disabled="!photoFile"
          @click="uploadPhoto"
        >
          <span v-if="loading">
            <i class="fas fa-spinner fa-spin"></i> Uploading...
          </span>
          <span v-else>Upload</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Photo View Dialog -->
  <div v-if="viewPhotoDialog" class="photo-viewer" @click="closePhotoDialog">
    <div
      class="photo-viewer-content"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
      @dblclick="handleDoubleTap"
    >
      <img
        v-if="selectedPhoto"
        :src="selectedPhoto.photoUrl"
        :alt="selectedPhoto.caption || 'Property Photo'"
        class="full-size-photo"
        :style="{
          transform: `scale(${photoScale}) translate(${photoTranslateX}px, ${photoTranslateY}px)`,
        }"
      />
      <div class="photo-viewer-caption" v-if="selectedPhoto">
        {{ selectedPhoto.caption || 'No caption' }}
        <button
          v-if="isEditing"
          class="edit-caption-button"
          @click.stop="openEditCaptionDialog"
        >
          <i class="fas fa-pencil-alt"></i>
        </button>
      </div>
      <div class="photo-viewer-close">tap to close</div>
    </div>
  </div>

  <!-- Edit Caption Dialog -->
  <div v-if="editCaptionDialog" class="dialog-overlay" @click.stop>
    <div class="dialog-content" @click.stop>
      <div class="dialog-header">Edit Caption</div>
      <div class="dialog-body">
        <input
          v-model="editingCaption"
          placeholder="Enter caption"
          class="caption-input"
        />
      </div>
      <div class="dialog-actions">
        <button @click="cancelEditCaption">Cancel</button>
        <button class="confirm-button" @click="updatePhotoCaption">Save</button>
      </div>
    </div>
  </div>

  <!-- Confirm Delete Dialog -->
  <div
    v-if="confirmDialog"
    class="dialog-overlay"
    @click="confirmDialog = false"
  >
    <div class="dialog-content" @click.stop>
      <div class="dialog-header">Confirm Delete</div>
      <div class="dialog-body">
        {{ confirmMessage }}
      </div>
      <div class="dialog-actions">
        <button
          @click="confirmDialog = false"
          class="confirm-dialog-button cancel-button"
        >
          Cancel
        </button>
        <button
          @click="confirmDelete"
          class="confirm-dialog-button delete-confirm-button"
        >
          Delete
        </button>
      </div>
    </div>
  </div>

  <!-- Upload Error Dialog -->
  <div
    v-if="showUploadErrorDialog"
    class="dialog-overlay"
    @click="showUploadErrorDialog = false"
  >
    <div class="dialog-content" @click.stop>
      <div class="dialog-header">Upload Error</div>
      <div class="dialog-body">
        <p>{{ uploadErrorMessage }}</p>
        <p v-if="uploadErrorDetails" class="error-details">
          <strong>Details:</strong> {{ uploadErrorDetails }}
        </p>
      </div>
      <div class="dialog-actions">
        <button @click="showUploadErrorDialog = false">Close</button>
      </div>
    </div>
  </div>

  <!-- In-Progress Note Dialog -->
  <div
    v-if="inProgressNoteDialog"
    class="dialog-overlay"
    @click="closeInProgressNoteDialog"
  >
    <div class="dialog-content" @click.stop>
      <div class="dialog-header">Add Note</div>
      <div class="dialog-body">
        <div v-if="selectedInProgressAppliance" class="appliance-info">
          <strong>{{ selectedInProgressAppliance.applianceType }}</strong>
          <br />
          <span class="property-name">{{
            selectedInProgressAppliance.propertyName
          }}</span>
        </div>
        <textarea
          v-model="inProgressNoteText"
          placeholder="Enter your note here..."
          class="note-textarea"
          rows="4"
        ></textarea>
      </div>
      <div class="dialog-actions">
        <button @click="closeInProgressNoteDialog">Cancel</button>
        <button class="confirm-button" @click="saveInProgressNote">
          Save Note
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';
import moment from 'moment';
import eventBus from './eventBus.js';
import MotdModal from './MotdModal.vue'; // Import MOTD Modal component
import { marked } from 'marked';
import {
  isLikelyImageFile,
  prepareImageForWorkOrderUpload,
  uploadFileLikeWorkOrder,
} from '@/utils/chunkedHeicUpload';
import { applianceDateCommit } from '@/utils/applianceDate';
import { sortAppliancesRemovedLast } from '@/utils/applianceList';

export default {
  components: { MotdModal },
  name: 'MAppliances',

  data() {
    return {
      // View management
      currentView: 'properties',
      selectedProperty: null,
      selectedAppliance: null,
      isEditing: false,
      dateFieldError: '',
      dateDialogError: '',

      // New variables for pinch to zoom on photos
      photoScale: 1,
      photoTranslateX: 0,
      photoTranslateY: 0,
      initialTouchDistance: 0,
      initialScale: 1,
      lastTouchX: 0,
      lastTouchY: 0,
      isPanning: false,
      isZooming: false,

      // Data lists
      properties: [],
      filteredProperties: [],
      appliances: [],
      photos: [],
      notes: [],
      recentPhotos: [], // Added for recent appliance photos carousel

      // Search
      searchQuery: '',
      searchInputRef: null,

      // Loading state
      loading: false,
      isLoadingRecentPhotos: false, // Added for recent photos loading state

      // Dialogs
      applianceDialog: false,
      photoDialog: false,
      viewPhotoDialog: false,
      confirmDialog: false,
      isEditMode: false,

      // Form data
      editedAppliance: {
        propertyId: null,
        make: '',
        model: '',
        serialNo: '',
        installDate: null,
        installDateISO: null,
        removeDate: null,
        removeDateISO: null,
        lastServiceDate: null,
        lastServiceDateISO: null,
        replaceAfter: 10,
        appliance_condition: 10,
        applianceType: '',
        customType: '',
        gasOrElectric: null,
        width: null,
        height: null,
        depth: null,
        requestedOn: null,
        requestedOnISO: null,
        requestedBy: null,
        dueBy: null,
        dueByISO: null,
        received: null,
        receivedISO: null,
        serviceOrReplace: null,
      },
      defaultApplianceData: {
        propertyId: null,
        make: '',
        model: '',
        serialNo: '',
        installDate: null,
        installDateISO: null,
        removeDate: null,
        removeDateISO: null,
        lastServiceDate: null,
        lastServiceDateISO: null,
        replaceAfter: 10,
        appliance_condition: 10,
        applianceType: '',
        customType: '',
        gasOrElectric: null,
        width: null,
        height: null,
        depth: null,
        requestedOn: null,
        requestedOnISO: null,
        requestedBy: null,
        dueBy: null,
        dueByISO: null,
        received: null,
        receivedISO: null,
        serviceOrReplace: null,
      },

      // Photo upload
      photoFile: null,
      photoFiles: [], // Updated to handle multiple files
      photoCaption: '',
      photoPreviewUrl: null,
      photoPreviewUrls: [], // Updated to handle multiple preview URLs
      selectedPhoto: null,
      editCaptionDialog: false,
      editingCaption: '',

      // Notes
      newNote: '',
      noteDocId: null, // Added for document linking
      editingNoteId: null, // ID of the note being edited
      editedNoteText: '', // Text of the note being edited
      editedNoteDocId: null, // Document ID linked to the note being edited

      // Confirm dialog
      confirmAction: null,
      confirmMessage: '',
      itemToDelete: null,

      // Coverage stats
      coverageStats: null,

      // In-progress appliances
      showInProgress: false,
      inProgressAppliances: [],
      loadingInProgress: false,

      // Add error dialog state variables
      showUploadErrorDialog: false,
      uploadErrorMessage: '',
      uploadErrorDetails: '',

      // Carousel auto-advance
      currentCarouselIndex: 0,
      carouselAutoAdvanceTimer: null,
      carouselProgressValue: 0,
      carouselTotalTime: 15000, // 15 seconds between auto-advances
      carouselProgressInterval: null,

      // In-progress note dialog
      inProgressNoteDialog: false,
      inProgressNoteText: '',
      selectedInProgressAppliance: null,
    };
  },

  computed: {
    isMobile() {
      return window.innerWidth <= 500;
    },
    sortedAppliances() {
      return sortAppliancesRemovedLast(this.appliances);
    },
  },

  created() {
    this.fetchProperties();
    this.fetchCoverageStats();
    this.fetchRecentPhotos(); // Added to fetch recent photos
    this.fetchInProgressAppliances(); // Added to fetch in-progress appliances

    // Start the auto-advance timer for the carousel
    this.startCarouselAutoAdvance();
  },

  mounted() {
    // Focus the search input on mount
    /*
    this.$refs.searchInput.focus();
    */
  },

  beforeUnmount() {
    // Clean up all blob URLs to prevent memory leaks
    this.cleanupBlobUrls();

    // Clear the carousel auto-advance timer
    this.stopCarouselAutoAdvance();
  },

  methods: {
    markedNote(note) {
      return marked(note);
    },

    // Helper methods for service/replace request functionality
    formatServiceOrReplace(value) {
      if (value === 1) return 'Service';
      if (value === 2) return 'Replace';
      return 'Not Set';
    },

    // In-progress appliances methods
    async toggleInProgress() {
      this.showInProgress = !this.showInProgress;
      if (this.showInProgress && this.inProgressAppliances.length === 0) {
        await this.fetchInProgressAppliances();
      }
    },

    async fetchInProgressAppliances() {
      this.loadingInProgress = true;
      try {
        const response = await axios.get('/api/appliances/in-progress');
        this.inProgressAppliances = response.data;
      } catch (error) {
        console.error('Error fetching in-progress appliances:', error);
        this.inProgressAppliances = [];
      } finally {
        this.loadingInProgress = false;
      }
    },

    async markAsReceived(applianceId) {
      try {
        await axios.put(`/api/appliances/${applianceId}/mark-received`);
        // Remove the appliance from the in-progress list
        this.inProgressAppliances = this.inProgressAppliances.filter(
          (app) => app.id !== applianceId,
        );

        // If we're viewing details of this appliance, refresh the details
        if (
          this.selectedAppliance &&
          this.selectedAppliance.id === applianceId
        ) {
          await this.fetchApplianceDetails(applianceId);
        }
      } catch (error) {
        console.error('Error marking appliance as received:', error);
        alert('Error marking appliance as received. Please try again.');
      }
    },

    // In-progress note dialog methods
    openInProgressNoteDialog(appliance) {
      this.selectedInProgressAppliance = appliance;
      this.inProgressNoteText = '';
      this.inProgressNoteDialog = true;
    },

    closeInProgressNoteDialog() {
      this.inProgressNoteDialog = false;
      this.selectedInProgressAppliance = null;
      this.inProgressNoteText = '';
    },

    async saveInProgressNote() {
      if (!this.inProgressNoteText.trim()) {
        alert('Please enter a note before saving.');
        return;
      }

      try {
        await axios.post(
          `/api/appliances/${this.selectedInProgressAppliance.id}/notes`,
          {
            note: this.inProgressNoteText.trim(),
          },
        );

        this.closeInProgressNoteDialog();
        // Show success message
        alert('Note saved successfully!');
      } catch (error) {
        console.error('Error saving note:', error);
        alert('Error saving note. Please try again.');
      }
    },

    handleTouchStart(event) {
      if (event.touches.length === 1) {
        // Single touch - prepare for panning
        this.isPanning = true;
        const touch = event.touches[0];
        this.lastTouchX = touch.clientX;
        this.lastTouchY = touch.clientY;
      } else if (event.touches.length === 2) {
        // Double touch - prepare for zooming
        this.isZooming = true;
        this.isPanning = false;

        // Calculate the initial distance between the two touches
        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        this.initialTouchDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY,
        );
        this.initialScale = this.photoScale;
      }
    },

    handleTouchMove(event) {
      event.preventDefault(); // Prevent browser gestures

      if (this.isZooming && event.touches.length === 2) {
        // Handle zooming - calculate new scale based on change in distance
        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        const currentDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY,
        );

        // Calculate new scale
        const newScale =
          this.initialScale * (currentDistance / this.initialTouchDistance);

        // Apply limits to the zoom scale
        this.photoScale = Math.min(Math.max(newScale, 1), 5);
      } else if (
        this.isPanning &&
        event.touches.length === 1 &&
        this.photoScale > 1
      ) {
        // Only allow panning when zoomed in
        const touch = event.touches[0];

        // Calculate the distance moved
        const deltaX = touch.clientX - this.lastTouchX;
        const deltaY = touch.clientY - this.lastTouchY;

        // Update the translations
        this.photoTranslateX += deltaX;
        this.photoTranslateY += deltaY;

        // Update the last touch position
        this.lastTouchX = touch.clientX;
        this.lastTouchY = touch.clientY;
      }
    },

    handleTouchEnd() {
      // Reset the touch states
      this.isPanning = false;
      this.isZooming = false;
    },

    handleDoubleTap(event) {
      if (this.photoScale > 1) {
        // If already zoomed in, reset to normal
        this.photoScale = 1;
        this.photoTranslateX = 0;
        this.photoTranslateY = 0;
      } else {
        // Zoom in to 2x at the tap location
        this.photoScale = 2;

        // Calculate tap position relative to the image center
        const img = event.target;
        const rect = img.getBoundingClientRect();
        const tapX = event.clientX - rect.left - rect.width / 2;
        const tapY = event.clientY - rect.top - rect.height / 2;

        // Center the zoomed view on the tap location
        this.photoTranslateX = -tapX;
        this.photoTranslateY = -tapY;
      }
    },

    closeAppliances() {
      this.applianceDialog = false;
      this.sendClose();
    },

    goBack() {
      window.history.back();
    },

    // Clean up blob URLs
    cleanupBlobUrls() {
      // Revoke all object URLs from photos
      if (this.photos && this.photos.length) {
        this.photos.forEach((photo) => {
          if (photo.photoUrl && typeof photo.photoUrl === 'string') {
            URL.revokeObjectURL(photo.photoUrl);
          }
        });
      }

      // Clean up any preview URLs
      if (this.photoPreviewUrls && this.photoPreviewUrls.length) {
        this.photoPreviewUrls.forEach((url) => {
          URL.revokeObjectURL(url);
        });
      }

      // Clean up recent photos blob URLs
      if (this.recentPhotos && this.recentPhotos.length) {
        this.recentPhotos.forEach((photo) => {
          if (photo.photoUrl && typeof photo.photoUrl === 'string') {
            URL.revokeObjectURL(photo.photoUrl);
          }
        });
      }
    },

    // Navigation methods
    sendClose() {
      // Clean up blob URLs before closing
      this.cleanupBlobUrls();
      eventBus.emit('requestCloseAppliances');
    },

    // Fetch coverage stats
    async fetchCoverageStats() {
      try {
        const response = await axios.get('/api/appliances-per-property');
        if (response.data && response.data.stats) {
          this.coverageStats = response.data.stats;
        }
      } catch (error) {
        console.error('Error fetching appliance coverage stats:', error);
      }
    },

    // Fetch recent photos
    async fetchRecentPhotos(forceRefresh = false) {
      this.isLoadingRecentPhotos = true;
      try {
        // Clear any old oversized cache on startup
        const cachedPhotosKey = 'recentAppliancePhotosCache';
        const cachedPhotosTimestampKey = 'recentAppliancePhotosCacheTimestamp';

        // Always clear cache - base64 caching caused quota issues
        localStorage.removeItem(cachedPhotosKey);
        localStorage.removeItem(cachedPhotosTimestampKey);

        // Clear existing blob URLs if forcing refresh
        if (forceRefresh) {
          console.log('Forcing refresh of recent photos');
          if (this.recentPhotos && this.recentPhotos.length) {
            this.recentPhotos.forEach((photo) => {
              if (photo.photoUrl) {
                URL.revokeObjectURL(photo.photoUrl);
              }
            });
          }
        }

        // Fetch fresh data from server
        const response = await axios.get('/api/recent-appliance-photos', {
          params: { nocache: forceRefresh ? Date.now() : undefined },
        });
        const photoPromises = response.data.map(async (photo) => {
          try {
            // Fetch the image as a blob
            const imageResponse = await axios.get(
              `/api/load-appliance-photo/${photo.id}?nocache=${forceRefresh ? Date.now() : ''}`,
              { responseType: 'blob' },
            );

            // Create an object URL from the blob
            const photoBlob = imageResponse.data;
            const photoUrl = URL.createObjectURL(photoBlob);

            // Convert blob to base64 for caching
            const base64Data = await this.blobToBase64(photoBlob);

            return {
              ...photo,
              photoUrl,
              blob: photoBlob,
              base64Data,
              contentType: photoBlob.type,
            };
          } catch (err) {
            // Missing files on disk (common in DEV when DB is shared but
            // Cabinet is incomplete) — avoid noisy console.error for 404s.
            if (err?.response?.status !== 404) {
              console.error(`Error fetching photo ${photo.id}:`, err);
            }
            return {
              ...photo,
              photoUrl: null,
              error: true,
            };
          }
        });

        this.recentPhotos = await Promise.all(photoPromises);
      } catch (error) {
        console.error('Error fetching recent photos:', error);
      } finally {
        this.isLoadingRecentPhotos = false;
      }

      // Check if the photo exists in recentPhotos.value array
      console.log('Photo load debug:', {
        recentPhotos: this.recentPhotos.length,
        isLoadingRecentPhotos: this.isLoadingRecentPhotos,
      });
    },

    // Helper function to convert blob to base64
    blobToBase64(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64data = reader.result.split(',')[1];
          resolve(base64data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    },

    // Carousel methods

    // Auto-advance carousel
    startCarouselAutoAdvance() {
      this.stopCarouselAutoAdvance(); // Ensure no duplicate timers

      // Reset progress value
      this.carouselProgressValue = 0;

      // Start progress bar interval
      this.carouselProgressInterval = setInterval(() => {
        // Increment the progress value
        this.carouselProgressValue += (100 / this.carouselTotalTime) * 100; // 100ms increment

        // If we reach 100%, move to the next image
        if (this.carouselProgressValue >= 100) {
          this.carouselProgressValue = 0;
          this.goToNextCarouselItem();
        }
      }, 100); // Update progress every 100ms for smooth animation

      // Main timer for auto-advance
      this.carouselAutoAdvanceTimer = setInterval(() => {
        this.goToNextCarouselItem();
      }, this.carouselTotalTime); // Advance every 15 seconds (set in data)
    },

    // Stop auto-advance
    stopCarouselAutoAdvance() {
      if (this.carouselAutoAdvanceTimer) {
        clearInterval(this.carouselAutoAdvanceTimer);
        this.carouselAutoAdvanceTimer = null;
      }

      if (this.carouselProgressInterval) {
        clearInterval(this.carouselProgressInterval);
        this.carouselProgressInterval = null;
      }

      // Reset progress
      this.carouselProgressValue = 0;
    },

    // Go to next carousel item with proper index tracking
    goToNextCarouselItem() {
      if (this.recentPhotos.length === 0) return;

      // Update the current index
      this.currentCarouselIndex =
        (this.currentCarouselIndex + 1) % this.recentPhotos.length;

      // Scroll the carousel
      this.scrollToCurrentItem();
    },

    // Scroll to the current item in the carousel
    scrollToCurrentItem() {
      const carousel = this.$refs.carouselInner;
      if (carousel) {
        const scrollAmount = this.currentCarouselIndex * 180; // Assuming each item is 180px wide
        carousel.scrollTo({ left: scrollAmount, behavior: 'smooth' });
      }
    },

    // Manual carousel navigation with index tracking
    prevCarouselPhoto(event) {
      if (event) event.stopPropagation();

      if (this.recentPhotos.length === 0) return;

      // Update the current index
      this.currentCarouselIndex =
        (this.currentCarouselIndex - 1 + this.recentPhotos.length) %
        this.recentPhotos.length;

      // Scroll the carousel
      this.scrollToCurrentItem();

      // Reset the auto-advance timer and progress
      this.startCarouselAutoAdvance();
    },

    nextCarouselPhoto(event) {
      if (event) event.stopPropagation();

      if (this.recentPhotos.length === 0) return;

      // Go to next item
      this.goToNextCarouselItem();

      // Reset the auto-advance timer and progress
      this.startCarouselAutoAdvance();
    },

    // Click on a specific dot to navigate
    goToCarouselItem(index) {
      if (index < 0 || index >= this.recentPhotos.length) return;

      this.currentCarouselIndex = index;
      this.scrollToCurrentItem();

      // Reset the auto-advance timer and progress
      this.startCarouselAutoAdvance();
    },

    // Open photo from carousel
    openPhotoFromCarousel(photo) {
      // Pause auto-advance while viewing photo
      this.stopCarouselAutoAdvance();

      this.selectedPhoto = photo;
      this.viewPhotoDialog = true;
    },

    // When closing the photo dialog, restart auto-advance
    closePhotoDialog() {
      this.viewPhotoDialog = false;
      this.photoScale = 1;
      this.photoTranslateX = 0;
      this.photoTranslateY = 0;

      // Restart auto-advance if we're on the main search screen
      if (this.currentView === 'properties' && !this.selectedProperty) {
        this.startCarouselAutoAdvance();
      }
    },

    filterProperties() {
      if (!this.searchQuery) {
        this.filteredProperties = [];
        return;
      }

      // Special case: Filter for properties without appliance records
      const query = this.searchQuery.toLowerCase().trim();
      if (query === ':u') {
        console.log('Special search for untracked properties');
        this.fetchUntrackedProperties();
        return;
      }

      // Mapping of common partial terms to their full appliance types
      const partialMatches = {
        refr: 'refrigerator',
        fridge: 'refrigerator',
        sto: 'stove',
        dish: 'dishwasher',
        micro: 'microwave',
        wash: 'washer',
        dry: 'dryer',
        ac: 'air conditioner',
        air: 'air conditioner',
        heat: 'heater',
      };

      // Replace commas with spaces, then split by spaces and filter out empty tokens
      const tokens = query
        .replace(/,/g, ' ')
        .split(' ')
        .map((token) => token.trim())
        .filter((token) => token);

      // Add the mapped full terms if there's a match with partial terms
      let expandedTokens = [...tokens];
      for (const token of tokens) {
        for (const [partial, full] of Object.entries(partialMatches)) {
          if (token.startsWith(partial) && !expandedTokens.includes(full)) {
            expandedTokens.push(full);
            break;
          }
        }
      }

      // Known appliance types for exact matches
      const knownApplianceTypes = [
        'refrigerator',
        'stove',
        'oven',
        'dishwasher',
        'freezer',
        'microwave',
        'washer',
        'dryer',
        'air conditioner',
        'heater',
      ];

      const isApplianceTypeSearch =
        knownApplianceTypes.some((type) => expandedTokens.includes(type)) ||
        expandedTokens.some((term) =>
          knownApplianceTypes.some((type) => type.includes(term)),
        );

      this.filteredProperties = this.properties.filter((property) => {
        // If searching for an appliance type, check appliances first
        if (
          isApplianceTypeSearch &&
          property.appliances &&
          property.appliances.length > 0
        ) {
          let hasTypeMatch = false;

          // Check each appliance against the expanded tokens
          for (const appliance of property.appliances) {
            const applianceType =
              appliance.applianceType === 'Other' && appliance.customType
                ? appliance.customType.toLowerCase()
                : appliance.applianceType.toLowerCase();

            for (const token of expandedTokens) {
              if (applianceType.includes(token)) {
                hasTypeMatch = true;
                break;
              }
            }

            if (hasTypeMatch) break;
          }

          if (hasTypeMatch) return true;
        }

        // For single search terms - just do a simple includes check
        if (tokens.length === 1) {
          return property.propertyAddress.toLowerCase().includes(tokens[0]);
        }

        // Otherwise, apply progressive token matching to the property address
        const fullText = property.propertyAddress.toLowerCase();
        let remainingText = fullText;

        // Check each token sequentially
        for (const token of tokens) {
          const index = remainingText.indexOf(token);
          if (index === -1) {
            // This token doesn't match the remaining text
            return false;
          }

          // "Remove" the matched part and everything before it from the remaining text
          remainingText = remainingText.substring(index + token.length);
        }

        return true;
      });

      // Sort filtered properties: exact matches first, then by address
      if (isApplianceTypeSearch) {
        this.filteredProperties.sort((a, b) => {
          const aHasExactMatch =
            a.appliances &&
            a.appliances.some((app) =>
              expandedTokens.some(
                (token) =>
                  app.applianceType.toLowerCase() === token ||
                  (app.applianceType === 'Other' &&
                    app.customType &&
                    app.customType.toLowerCase() === token),
              ),
            );
          const bHasExactMatch =
            b.appliances &&
            b.appliances.some((app) =>
              expandedTokens.some(
                (token) =>
                  app.applianceType.toLowerCase() === token ||
                  (app.applianceType === 'Other' &&
                    app.customType &&
                    app.customType.toLowerCase() === token),
              ),
            );

          if (aHasExactMatch && !bHasExactMatch) return -1;
          if (!aHasExactMatch && bHasExactMatch) return 1;
          return a.propertyAddress.localeCompare(b.propertyAddress);
        });
      }
    },

    // Fetch properties without appliance records
    async fetchUntrackedProperties() {
      console.log('Fetching untracked properties');
      this.loading = true;
      try {
        const response = await axios.get('/api/appliances-per-property', {
          params: { untracked: 'true' },
        });
        console.log('Response received:', response.data);
        if (response.data && response.data.properties) {
          this.filteredProperties = response.data.properties;
        }
        this.loading = false;
      } catch (error) {
        console.error('Error fetching untracked properties:', error);
        this.loading = false;
      }
    },

    clearSearch() {
      this.searchQuery = '';
      this.selectedProperty = null;
      this.filteredProperties = [];
    },

    // Term highlighting
    highlightMatch(text, queryInput) {
      if (queryInput) {
        const tokens = queryInput.split(',').map((token) => token.trim());
        const query =
          tokens.length === 1 ? tokens[0] : tokens[tokens.length - 1];
        const escapedQuery = query.replace(/[-/e^$*+?.()|[\]{}]/g, '\\$&');
        const regex = new RegExp(`(${escapedQuery})`, 'gi');
        if (text) {
          return text.replace(regex, '<span class="highlight">$1</span>');
        }
      }
      return text;
    },

    // Format address to use "Apt" instead of "#"
    formatAddress(address) {
      if (!address) return '';
      // Replace "#123" pattern with "Apt 123"
      return address.replace(/#(\d+)/g, 'Apt $1');
    },

    // Data management methods
    async fetchProperties() {
      this.loading = true;
      try {
        const response = await axios.get('/api/properties-with-appliances');
        // Get all properties first
        let properties = response.data;

        // For each property with appliances, fetch the appliance details
        const propertiesWithAppliances = await Promise.all(
          properties.map(async (property) => {
            if (property.applianceCount > 0) {
              try {
                const appliancesResponse = await axios.get(
                  `/api/appliances/${property.id}`,
                );
                return { ...property, appliances: appliancesResponse.data };
              } catch (error) {
                console.error(
                  `Error fetching appliances for property ${property.id}:`,
                  error,
                );
                return { ...property, appliances: [] };
              }
            }
            return { ...property, appliances: [] };
          }),
        );

        this.properties = propertiesWithAppliances;
        this.loading = false;
      } catch (error) {
        console.error('Error fetching properties:', error);
        this.loading = false;
      }
    },

    async fetchAppliancesForProperty(propertyId) {
      this.loading = true;
      try {
        const response = await axios.get(`/api/appliances/${propertyId}`);
        this.appliances = response.data;
        this.loading = false;
      } catch (error) {
        console.error('Error fetching appliances:', error);
        this.loading = false;
      }
    },

    async fetchApplianceDetails(applianceId) {
      this.loading = true;
      try {
        const response = await axios.get(`/api/appliance/${applianceId}`);
        this.selectedAppliance = response.data;

        // Convert dates to ISO format for HTML date inputs
        if (this.selectedAppliance.installDate) {
          this.selectedAppliance.installDateISO = moment(
            this.selectedAppliance.installDate,
          ).format('YYYY-MM-DD');
        }
        if (this.selectedAppliance.removeDate) {
          this.selectedAppliance.removeDateISO = moment(
            this.selectedAppliance.removeDate,
          ).format('YYYY-MM-DD');
        }
        if (this.selectedAppliance.lastServiceDate) {
          this.selectedAppliance.lastServiceDateISO = moment(
            this.selectedAppliance.lastServiceDate,
          ).format('YYYY-MM-DD');
        }

        // Convert service/replace request date fields to ISO format for HTML date inputs
        if (this.selectedAppliance.requestedOn) {
          const requestedOnDate = new Date(this.selectedAppliance.requestedOn);
          this.selectedAppliance.requestedOnISO =
            moment(requestedOnDate).format('YYYY-MM-DD');
        }
        if (this.selectedAppliance.dueBy) {
          const dueByDate = new Date(this.selectedAppliance.dueBy);
          this.selectedAppliance.dueByISO =
            moment(dueByDate).format('YYYY-MM-DD');
        }
        if (this.selectedAppliance.received) {
          const receivedDate = new Date(this.selectedAppliance.received);
          this.selectedAppliance.receivedISO =
            moment(receivedDate).format('YYYY-MM-DD');
        }

        await this.fetchPhotos(applianceId);
        await this.fetchNotes(applianceId);
        this.loading = false;
      } catch (error) {
        console.error('Error fetching appliance details:', error);
        this.loading = false;
      }
    },

    async fetchPhotos(applianceId) {
      try {
        // Step 1: Get the list of photos from the API
        const response = await axios.get(
          `/api/appliances/${applianceId}/photos`,
        );

        // Step 2: For each photo, fetch the actual image data as a blob
        const photoPromises = response.data.map(async (photo) => {
          try {
            // Fetch the image as a blob using our secure endpoint
            const imageResponse = await axios.get(
              `/api/load-appliance-photo/${photo.id}`,
              { responseType: 'blob' },
            );

            // Create an object URL from the blob
            const photoBlob = imageResponse.data;
            const photoUrl = URL.createObjectURL(photoBlob);

            // Return the photo with the blob URL
            return {
              ...photo,
              photoUrl: photoUrl,
              // Store the blob to properly revoke the URL later
              blob: photoBlob,
            };
          } catch (err) {
            if (err?.response?.status !== 404) {
              console.error(`Error fetching photo ${photo.id}:`, err);
            }
            // Return the photo without a URL if fetching fails
            return {
              ...photo,
              photoUrl: null,
              error: true,
            };
          }
        });

        // Wait for all photo fetches to complete
        this.photos = await Promise.all(photoPromises);
      } catch (error) {
        console.error('Error fetching photos:', error);
        this.photos = [];
      }
    },

    async fetchNotes(applianceId) {
      try {
        const response = await axios.get(
          `/api/appliances/${applianceId}/notes`,
        );
        this.notes = response.data;
      } catch (error) {
        console.error('Error fetching notes:', error);
      }
    },

    // Selection and navigation
    selectProperty(property) {
      this.selectedProperty = property;
      this.fetchAppliancesForProperty(property.id);

      // Collapse the service/replace dropdown when selecting a property
      this.showInProgress = false;
    },

    backToProperties() {
      this.selectedProperty = null;
      this.appliances = [];
      this.searchQuery = '';
      this.filteredProperties = [];
    },

    viewApplianceDetails(appliance) {
      this.dateFieldError = '';
      this.fetchApplianceDetails(appliance.id);
      this.currentView = 'details';
    },

    backToAppliances() {
      this.currentView = 'properties';
      this.selectedAppliance = null;
      this.photos = [];
      this.notes = [];
      this.isEditing = false;
      this.dateFieldError = '';

      // Refresh the appliances list when returning to ensure counts are up-to-date
      if (this.selectedProperty) {
        this.fetchAppliancesForProperty(this.selectedProperty.id);
      }
    },

    // Edit mode toggle
    toggleEditMode() {
      // If we're currently in edit mode and switching to view mode, save changes
      if (this.isEditing) {
        const install = applianceDateCommit(
          this.selectedAppliance && this.selectedAppliance.installDateISO,
        );
        const remove = applianceDateCommit(
          this.selectedAppliance && this.selectedAppliance.removeDateISO,
        );
        if (!install.ok || !remove.ok) {
          this.dateFieldError = (!install.ok ? install : remove).error;
          return;
        }
        this.dateFieldError = '';
        if (this.selectedAppliance) {
          this.selectedAppliance.installDate = install.display;
          this.selectedAppliance.removeDate = remove.display;
        }
        this.saveAllApplianceChanges();
      }

      this.isEditing = !this.isEditing;

      // When exiting edit mode:
      if (!this.isEditing) {
        // Refresh the appliances list
        if (this.selectedProperty) {
          this.fetchAppliancesForProperty(this.selectedProperty.id);
        }

        // Scroll the details container back to the top
        this.$nextTick(() => {
          const detailsContainer = document.querySelector(
            '.details-form-container',
          );
          if (detailsContainer) {
            detailsContainer.scrollTop = 0;
          }
        });
      }
    },

    // New method to save all changes at once
    async saveAllApplianceChanges() {
      if (!this.selectedAppliance) return;

      try {
        console.log('Saving all appliance changes to server...');

        // Create a copy of the data to update
        const updateData = { ...this.selectedAppliance };

        // Process dates properly
        if (updateData.installDate) {
          updateData.installDate = moment(updateData.installDate).format(
            'YYYY-MM-DD',
          );
        }
        if (updateData.removeDate) {
          updateData.removeDate = moment(updateData.removeDate).format(
            'YYYY-MM-DD',
          );
        }
        if (updateData.lastServiceDate) {
          updateData.lastServiceDate = moment(
            updateData.lastServiceDate,
          ).format('YYYY-MM-DD');
        }
        if (updateData.requestedOn) {
          updateData.requestedOn = moment(updateData.requestedOn).format(
            'YYYY-MM-DD',
          );
        }
        if (updateData.dueBy) {
          updateData.dueBy = moment(updateData.dueBy).format('YYYY-MM-DD');
        }
        if (updateData.received) {
          updateData.received = moment(updateData.received).format(
            'YYYY-MM-DD',
          );
        }

        // Cleanup temporary ISO date fields
        delete updateData.installDateISO;
        delete updateData.removeDateISO;
        delete updateData.lastServiceDateISO;
        delete updateData.requestedOnISO;
        delete updateData.dueByISO;
        delete updateData.receivedISO;

        console.log('Sending update with data:', updateData);

        // Send the update to the server
        const response = await axios.put(
          `/api/appliances/${this.selectedAppliance.id}`,
          updateData,
        );

        console.log('Update response:', response.data);

        // Refresh the in-progress list if it's visible
        if (this.showInProgress) {
          await this.fetchInProgressAppliances();
        }
      } catch (error) {
        console.error('Error saving appliance changes:', error);
        if (error.response) {
          console.error('Response status:', error.response.status);
          console.error('Response data:', error.response.data);
        }
        alert(
          'Error saving changes: ' + (error.message || 'Please try again.'),
        );
      }
    },

    setCondition(value) {
      if (this.isEditing) {
        this.selectedAppliance.appliance_condition = value;
        this.updateAppliance('appliance_condition');
      }
    },

    // Update methods
    async updateAppliance(field) {
      if (!this.selectedAppliance) return;

      try {
        // Create a copy of the data to update and handle date formatting
        const updateData = {};

        // Format dates properly if this is a date field
        if (
          field === 'installDate' ||
          field === 'removeDate' ||
          field === 'lastServiceDate'
        ) {
          // Convert date from MM/DD/YYYY to YYYY-MM-DD format for MySQL
          if (this.selectedAppliance[field]) {
            const parsedDate = moment(
              this.selectedAppliance[field],
              'MM/DD/YYYY',
            );
            if (parsedDate.isValid()) {
              updateData[field] = parsedDate.format('YYYY-MM-DD');
            } else {
              throw new Error(
                'Invalid date format. Please use MM/DD/YYYY format.',
              );
            }
          } else {
            updateData[field] = null;
          }
        } else if (field === 'serviceOrReplace') {
          // Explicitly handle serviceOrReplace field to ensure null is properly sent
          updateData[field] = this.selectedAppliance[field];
          console.log(`Updating serviceOrReplace to: ${updateData[field]}`);
        } else if (
          field === 'requestedOn' ||
          field === 'dueBy' ||
          field === 'received'
        ) {
          // Handle date fields for service/replace request
          if (this.selectedAppliance[field]) {
            updateData[field] = moment(this.selectedAppliance[field]).format(
              'YYYY-MM-DD',
            );
          } else {
            updateData[field] = null;
          }
        } else {
          // For non-date fields, use as is
          updateData[field] = this.selectedAppliance[field];
        }

        await axios.put(
          `/api/appliances/${this.selectedAppliance.id}`,
          updateData,
        );

        // Refresh the in-progress list if it's visible
        if (this.showInProgress) {
          await this.fetchInProgressAppliances();
        }
      } catch (error) {
        console.error('Error updating appliance:', error);
        if (field === 'installDate' || field === 'removeDate') {
          const serverMessage =
            (error.response &&
              error.response.data &&
              (error.response.data.error || error.response.data.message)) ||
            error.message ||
            '';
          this.dateFieldError = /Unknown column 'removeDate'/i.test(
            String(serverMessage),
          )
            ? 'Remove date is not in the database yet.'
            : 'Could not save that date.';
          return;
        }
        alert(
          'Error updating appliance: ' + (error.message || 'Please try again.'),
        );
      }
    },

    validateAndUpdateDate(field) {
      const dateValue = this.selectedAppliance[field];
      const isValidDate = moment(dateValue, 'MM/DD/YYYY', true).isValid();

      if (isValidDate || !dateValue) {
        this.updateAppliance(field);
      } else {
        alert('Please enter a valid date in MM/DD/YYYY format.');
      }
    },

    updateCustomType() {
      if (!this.selectedAppliance) return;

      try {
        axios.put(`/api/appliances/${this.selectedAppliance.id}`, {
          customType: this.selectedAppliance.customType,
        });
      } catch (error) {
        console.error('Error updating custom type:', error);
      }
    },

    handleTypeChange() {
      if (this.selectedAppliance.applianceType !== 'Other') {
        this.selectedAppliance.customType = '';
      }
    },

    handleEditedTypeChange() {
      if (this.editedAppliance.applianceType !== 'Other') {
        this.editedAppliance.customType = '';
      }
    },

    commitSelectedDate(field) {
      if (!this.selectedAppliance) return;
      if (field !== 'installDate' && field !== 'removeDate') return;
      const result = applianceDateCommit(this.selectedAppliance[`${field}ISO`]);
      if (!result.ok) {
        this.dateFieldError = result.error;
        return;
      }
      this.dateFieldError = '';
      this.selectedAppliance[field] = result.display;
      this.updateAppliance(field);
    },

    commitEditedDate(field) {
      if (field !== 'installDate' && field !== 'removeDate') return;
      const result = applianceDateCommit(this.editedAppliance[`${field}ISO`]);
      if (!result.ok) {
        this.dateDialogError = result.error;
        return;
      }
      this.dateDialogError = '';
      this.editedAppliance[field] = result.display;
    },

    handleDateChange(field) {
      if (field === 'lastServiceDate') {
        this.selectedAppliance.lastServiceDate = moment(
          this.selectedAppliance.lastServiceDateISO,
        ).format('MM/DD/YYYY');
        this.updateAppliance('lastServiceDate');
      } else if (field === 'requestedOn') {
        this.selectedAppliance.requestedOn = moment(
          this.selectedAppliance.requestedOnISO,
        ).format('MM/DD/YYYY');
        this.updateAppliance('requestedOn');
      } else if (field === 'dueBy') {
        this.selectedAppliance.dueBy = moment(
          this.selectedAppliance.dueByISO,
        ).format('MM/DD/YYYY');
        this.updateAppliance('dueBy');
      } else if (field === 'received') {
        this.selectedAppliance.received = moment(
          this.selectedAppliance.receivedISO,
        ).format('MM/DD/YYYY');
        this.updateAppliance('received');
      }
    },

    handleEditedDateChange(field) {
      if (field === 'lastServiceDate') {
        this.editedAppliance.lastServiceDate = moment(
          this.editedAppliance.lastServiceDateISO,
        ).format('MM/DD/YYYY');
      } else if (field === 'requestedOn') {
        this.editedAppliance.requestedOn = moment(
          this.editedAppliance.requestedOnISO,
        ).format('MM/DD/YYYY');
      } else if (field === 'dueBy') {
        this.editedAppliance.dueBy = moment(
          this.editedAppliance.dueByISO,
        ).format('MM/DD/YYYY');
      } else if (field === 'received') {
        this.editedAppliance.received = moment(
          this.editedAppliance.receivedISO,
        ).format('MM/DD/YYYY');
      }
    },

    // Dialog management
    openApplianceDialog(appliance = null) {
      this.dateDialogError = '';
      this.isEditMode = !!appliance;

      if (appliance) {
        this.editedAppliance = { ...appliance };

        // Convert dimension strings to numbers for proper input field display
        if (appliance.width !== null && appliance.width !== undefined) {
          this.editedAppliance.width = parseFloat(appliance.width) || null;
        }
        if (appliance.height !== null && appliance.height !== undefined) {
          this.editedAppliance.height = parseFloat(appliance.height) || null;
        }
        if (appliance.depth !== null && appliance.depth !== undefined) {
          this.editedAppliance.depth = parseFloat(appliance.depth) || null;
        }

        // Convert existing date values to ISO format for date pickers
        if (appliance.installDate && !this.editedAppliance.installDateISO) {
          this.editedAppliance.installDateISO = moment(
            appliance.installDate,
            'MM/DD/YYYY',
          ).format('YYYY-MM-DD');
        }
        if (appliance.removeDate && !this.editedAppliance.removeDateISO) {
          this.editedAppliance.removeDateISO = moment(
            appliance.removeDate,
            'MM/DD/YYYY',
          ).format('YYYY-MM-DD');
        }
        if (
          appliance.lastServiceDate &&
          !this.editedAppliance.lastServiceDateISO
        ) {
          this.editedAppliance.lastServiceDateISO = moment(
            appliance.lastServiceDate,
            'MM/DD/YYYY',
          ).format('YYYY-MM-DD');
        }
        // For service/replace request dates, handle both database format (YYYY-MM-DD HH:mm:ss) and display format (MM/DD/YYYY)
        if (appliance.requestedOn && !this.editedAppliance.requestedOnISO) {
          // Try to parse as database datetime format first, then fallback to MM/DD/YYYY
          const requestedOnMoment = moment(appliance.requestedOn).isValid()
            ? moment(appliance.requestedOn)
            : moment(appliance.requestedOn, 'MM/DD/YYYY');
          this.editedAppliance.requestedOnISO =
            requestedOnMoment.format('YYYY-MM-DD');
        }
        if (appliance.dueBy && !this.editedAppliance.dueByISO) {
          const dueByMoment = moment(appliance.dueBy).isValid()
            ? moment(appliance.dueBy)
            : moment(appliance.dueBy, 'MM/DD/YYYY');
          this.editedAppliance.dueByISO = dueByMoment.format('YYYY-MM-DD');
        }
        if (appliance.received && !this.editedAppliance.receivedISO) {
          const receivedMoment = moment(appliance.received).isValid()
            ? moment(appliance.received)
            : moment(appliance.received, 'MM/DD/YYYY');
          this.editedAppliance.receivedISO =
            receivedMoment.format('YYYY-MM-DD');
        }
      } else {
        this.editedAppliance = { ...this.defaultApplianceData };
        this.editedAppliance.propertyId = this.selectedProperty.id;
      }

      this.applianceDialog = true;
    },

    openPhotoUploadDialog() {
      this.photoFile = null;
      this.photoFiles = []; // Reset photoFiles array
      this.photoCaption = '';
      this.photoPreviewUrl = null;
      this.photoPreviewUrls = []; // Reset photoPreviewUrls array
      this.photoDialog = true;
    },

    openPhotoDialog(photo) {
      this.selectedPhoto = photo;
      this.viewPhotoDialog = true;
    },

    openEditCaptionDialog(event) {
      event.stopPropagation();
      if (this.selectedPhoto) {
        this.editingCaption = this.selectedPhoto.caption || '';
        this.editCaptionDialog = true;
      }
    },

    cancelEditCaption() {
      this.editCaptionDialog = false;
    },

    async updatePhotoCaption() {
      if (!this.selectedPhoto) return;

      try {
        await axios.put(
          `/api/appliance-photos/${this.selectedPhoto.id}/caption`,
          {
            caption: this.editingCaption,
          },
        );

        // Update the caption in the local photo object
        this.selectedPhoto.caption = this.editingCaption;

        // Also update in the photos array
        const photoIndex = this.photos.findIndex(
          (p) => p.id === this.selectedPhoto.id,
        );
        if (photoIndex !== -1) {
          this.photos[photoIndex].caption = this.editingCaption;
        }

        this.editCaptionDialog = false;
      } catch (error) {
        console.error('Error updating photo caption:', error);
        alert('Error updating caption. Please try again.');
      }
    },

    handlePhotoSelect(event) {
      const files = Array.from(event.target.files);
      if (files.length) {
        const invalidFiles = files.filter((file) => !isLikelyImageFile(file));
        if (invalidFiles.length) {
          alert('Please select only image files');
          return;
        }

        this.photoFiles = files;
        // Store the first file as photoFile for backward compatibility
        this.photoFile = files[0];
        // Generate preview URLs for all selected files
        this.photoPreviewUrls = files.map((file) => URL.createObjectURL(file));
        // Store the first preview URL as photoPreviewUrl for backward compatibility
        this.photoPreviewUrl = this.photoPreviewUrls[0];
      }
    },

    async uploadPhoto() {
      if (!this.photoFiles.length || !this.selectedAppliance) return;
      try {
        this.loading = true;
        const applianceId = this.selectedAppliance.id;
        const caption = this.photoCaption;

        for (const file of this.photoFiles) {
          const prepared = await prepareImageForWorkOrderUpload(file);
          await uploadFileLikeWorkOrder(prepared.file, prepared.isHeic, {
            singleUrl: `/api/appliances/${applianceId}/photos`,
            fillSingleForm: (formData) => {
              formData.append('caption', caption);
              formData.append('applianceId', applianceId);
            },
            initUrl: '/api/appliances/chunked/init',
            initBody: { applianceId, caption },
            chunkUrl: '/api/appliances/chunked/upload',
            finalizeUrl: '/api/appliances/chunked/finalize',
          });
        }

        // Refresh photos after upload
        await this.fetchPhotos(this.selectedAppliance.id);
        this.photoDialog = false;
        this.loading = false;
      } catch (error) {
        console.error('Error processing or uploading photo:', error);
        this.loading = false;

        // Show detailed error dialog instead of simple alert
        this.uploadErrorMessage = 'Error uploading photo';
        this.uploadErrorDetails = error.response
          ? `${error.response.status}: ${error.response.data.error || error.response.data.message || JSON.stringify(error.response.data)}`
          : error.message || 'Unknown error occurred';
        this.showUploadErrorDialog = true;
      }
    },

    confirmDeletePhoto(photo) {
      this.confirmMessage = 'Are you sure you want to delete this photo?';
      this.confirmAction = 'deletePhoto';
      this.itemToDelete = photo;
      this.confirmDialog = true;
      // Stop event propagation to prevent opening the photo preview
      event.stopPropagation();
    },

    async deletePhoto(photoId) {
      try {
        await axios.delete(`/api/appliance-photos/${photoId}`);
        await this.fetchPhotos(this.selectedAppliance.id);
      } catch (error) {
        console.error('Error deleting photo:', error);
        alert('Error deleting photo. Please try again.');
      }
    },

    scrollCarousel(direction) {
      const carousel = this.$refs.carouselInner;
      if (carousel) {
        const scrollAmount = direction === 'left' ? -180 : 180;
        carousel.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
    },

    async addNote() {
      if (!this.newNote.trim()) return;

      try {
        await axios.post(`/api/appliances/${this.selectedAppliance.id}/notes`, {
          note: this.newNote,
          docId: this.noteDocId, // Include docId in the note
        });

        this.newNote = '';
        this.noteDocId = null; // Reset docId after saving
        await this.fetchNotes(this.selectedAppliance.id);
      } catch (error) {
        console.error('Error adding note:', error);
      }
    },

    async editNote(note) {
      this.editingNoteId = note.id;
      this.editedNoteText = note.note;
      this.editedNoteDocId = note.docId || null;
    },

    async updateNote(note) {
      if (!this.editedNoteText.trim()) return;

      try {
        await axios.put(`/api/appliance-notes/${note.id}`, {
          note: this.editedNoteText,
          docId: this.editedNoteDocId,
        });

        this.editingNoteId = null;
        this.editedNoteText = '';
        this.editedNoteDocId = null;
        await this.fetchNotes(this.selectedAppliance.id);
      } catch (error) {
        console.error('Error updating note:', error);
      }
    },

    cancelEditNote() {
      this.editingNoteId = null;
      this.editedNoteText = '';
      this.editedNoteDocId = null;
    },

    // Confirm dialogs
    confirmDeleteAppliance(appliance) {
      this.confirmMessage = `Are you sure you want to delete the ${appliance.make} ${appliance.model} appliance?`;
      this.confirmAction = 'deleteAppliance';
      this.itemToDelete = appliance;
      this.confirmDialog = true;
    },

    confirmDeleteNote(note) {
      this.confirmMessage = 'Are you sure you want to delete this note?';
      this.confirmAction = 'deleteNote';
      this.itemToDelete = note;
      this.confirmDialog = true;
    },

    async confirmDelete() {
      try {
        if (this.confirmAction === 'deleteAppliance') {
          await axios.delete(`/api/appliances/${this.itemToDelete.id}`);
          if (this.currentView === 'details') {
            this.backToAppliances();
          } else {
            await this.fetchAppliancesForProperty(this.selectedProperty.id);
          }
        } else if (this.confirmAction === 'deletePhoto') {
          await axios.delete(`/api/appliance-photos/${this.itemToDelete.id}`);
          await this.fetchPhotos(this.selectedAppliance.id);
        } else if (this.confirmAction === 'deleteNote') {
          await axios.delete(`/api/appliance-notes/${this.itemToDelete.id}`);
          await this.fetchNotes(this.selectedAppliance.id);
        }

        this.confirmDialog = false;
      } catch (error) {
        console.error('Error deleting item:', error);
      }
    },

    // Save appliance method (was missing)
    async saveAppliance() {
      try {
        this.loading = true;

        // Create a copy of the data to send and format dates properly
        const applianceData = { ...this.editedAppliance };
        console.log('Initial appliance data before formatting:', applianceData);

        // Ensure applianceType is included
        if (!applianceData.applianceType) {
          console.log('Setting default applianceType to "Other"');
          applianceData.applianceType = 'Other'; // Default to Other if not specified
        }

        // If type is "Other", make sure we have the customType
        if (
          applianceData.applianceType === 'Other' &&
          !applianceData.customType
        ) {
          console.log('Setting default customType to "Unspecified"');
          applianceData.customType = 'Unspecified'; // Set a default for customType
        }

        const installCommit = applianceDateCommit(applianceData.installDateISO);
        const removeCommit = applianceDateCommit(applianceData.removeDateISO);
        if (!installCommit.ok || !removeCommit.ok) {
          this.dateDialogError = (
            !installCommit.ok ? installCommit : removeCommit
          ).error;
          this.loading = false;
          return;
        }
        this.dateDialogError = '';
        applianceData.installDate = installCommit.iso;
        applianceData.removeDate = removeCommit.iso;

        console.log(
          'Processing lastServiceDate:',
          applianceData.lastServiceDate,
        );
        if (applianceData.lastServiceDate) {
          const parsedDate = moment(
            applianceData.lastServiceDate,
            'MM/DD/YYYY',
          );
          console.log('Parsed lastServiceDate valid:', parsedDate.isValid());
          if (parsedDate.isValid()) {
            applianceData.lastServiceDate = parsedDate.format('YYYY-MM-DD');
            console.log(
              'Formatted lastServiceDate:',
              applianceData.lastServiceDate,
            );
          } else {
            console.log('Invalid lastServiceDate format');
            throw new Error(
              'Invalid Last Service Date format. Please use MM/DD/YYYY.',
            );
          }
        } else if (applianceData.lastServiceDateISO) {
          // Try using ISO date if regular date is not available
          console.log(
            'Using lastServiceDateISO instead:',
            applianceData.lastServiceDateISO,
          );
          applianceData.lastServiceDate = applianceData.lastServiceDateISO;
        }

        // Clean up temporary ISO date fields
        delete applianceData.installDateISO;
        delete applianceData.removeDateISO;
        delete applianceData.lastServiceDateISO;

        // Process service/replace request date fields - convert from date to MySQL format
        console.log('Processing service/replace request date fields...');

        // Debug service/replace value explicitly
        console.log(
          'Processing serviceOrReplace value:',
          applianceData.serviceOrReplace,
        );

        if (applianceData.requestedOn) {
          console.log('Processing requestedOn:', applianceData.requestedOn);
          // For date inputs, just format as YYYY-MM-DD for MySQL DATE fields
          const requestedOnDate = new Date(applianceData.requestedOn);
          applianceData.requestedOn =
            moment(requestedOnDate).format('YYYY-MM-DD');
        }
        if (applianceData.dueBy) {
          console.log('Processing dueBy:', applianceData.dueBy);
          const dueByDate = new Date(applianceData.dueBy);
          applianceData.dueBy = moment(dueByDate).format('YYYY-MM-DD');
        }
        if (applianceData.received) {
          console.log('Processing received:', applianceData.received);
          const receivedDate = new Date(applianceData.received);
          applianceData.received = moment(receivedDate).format('YYYY-MM-DD');
        }

        // Clean up temporary ISO datetime fields
        delete applianceData.requestedOnISO;
        delete applianceData.dueByISO;
        delete applianceData.receivedISO;

        // console.log('Final appliance data to be sent:', applianceData);

        // If we're editing an existing appliance
        if (this.isEditMode && applianceData.id) {
          // console.log( `Updating existing appliance with ID ${applianceData.id}`,);
          const response = await axios.put(
            `/api/appliances/${applianceData.id}`,
            applianceData,
          );
          console.log('Update response:', response.data);

          // Refresh the appliance details if we're in details view
          if (
            this.currentView === 'details' &&
            this.selectedAppliance &&
            this.selectedAppliance.id === applianceData.id
          ) {
            await this.fetchApplianceDetails(applianceData.id);
          }

          // Refresh the appliances list if needed
          if (this.currentView === 'properties' && this.selectedProperty) {
            await this.fetchAppliancesForProperty(this.selectedProperty.id);
          }

          // Refresh the in-progress list if it's visible
          if (this.showInProgress) {
            await this.fetchInProgressAppliances();
          }
        }
        // If we're adding a new appliance
        else if (this.selectedProperty) {
          console.log(
            `Adding new appliance for property ID ${this.selectedProperty.id}`,
          );
          const response = await axios.post('/api/appliances', applianceData);
          console.log('Create response:', response.data);

          // Fetch appliances for the current property
          await this.fetchAppliancesForProperty(this.selectedProperty.id);

          // Refresh the in-progress list if it's visible
          if (this.showInProgress) {
            await this.fetchInProgressAppliances();
          }

          // Update the property in the properties array with the new appliance data
          await this.updatePropertyWithNewAppliance(
            this.selectedProperty.id,
            response.data.id,
            applianceData,
          );
        } else {
          console.error('Cannot save appliance: No property selected');
        }

        this.applianceDialog = false;
        this.loading = false;
      } catch (error) {
        console.error('Error saving appliance:', error);
        if (error.response) {
          console.error('Response status:', error.response.status);
          console.error('Response data:', error.response.data);
        }
        this.loading = false;
        alert(
          'Error saving appliance: ' + (error.message || 'Please try again.'),
        );
      }
    },

    // Helper methods
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return moment(dateString).format('MM/DD/YYYY');
    },

    calculateApplianceAge(installDate) {
      if (!installDate) return '';

      const today = moment();
      const installed = moment(installDate);

      // Calculate years and months
      const years = today.diff(installed, 'years');
      installed.add(years, 'years'); // Add the years to get remaining months
      const months = today.diff(installed, 'months');

      // Format the age string
      let ageStr = '';
      if (years > 0) {
        ageStr += `${years} yr`;
        if (years > 1) ageStr += 's';
      }

      if (months > 0) {
        if (ageStr) ageStr += ' ';
        ageStr += `${months} mo`;
        if (months > 1) ageStr += 's';
      }

      // If installed today or in the future
      if (ageStr === '') {
        return 'New';
      }

      return `${ageStr} ago`;
    },

    formatDateTime(dateString) {
      if (!dateString) return '';
      return moment(dateString).format('MM/DD/YYYY h:mm A');
    },

    calculateReplacementDue(installDate, replaceAfter) {
      if (!installDate || !replaceAfter) return 'N/A';

      const replacementDate = moment(installDate).add(replaceAfter, 'years');
      const now = moment();

      if (replacementDate < now) {
        return `Overdue since ${replacementDate.format('MM/DD/YYYY')}`;
      } else {
        const daysRemaining = replacementDate.diff(now, 'days');
        return `${replacementDate.format('MM/DD/YYYY')} (${daysRemaining} days remaining)`;
      }
    },

    isApplianceTypeSearch() {
      const query = this.searchQuery.toLowerCase().trim();

      // Mapping of common partial terms to their full appliance types
      const partialMatches = {
        refr: 'refrigerator',
        fridge: 'refrigerator',
        stv: 'stove',
        dish: 'dishwasher',
        micro: 'microwave',
        wash: 'washer',
        dry: 'dryer',
        ac: 'air conditioner',
        air: 'air conditioner',
        heat: 'heater',
      };

      // Known appliance types for exact matches
      const knownApplianceTypes = [
        'refrigerator',
        'stove',
        'oven',
        'dishwasher',
        'freezer',
        'microwave',
        'washer',
        'dryer',
        'air conditioner',
        'heater',
      ];

      // Check for partial matches
      for (const [partial] of Object.entries(partialMatches)) {
        if (query.startsWith(partial)) {
          return true;
        }
      }

      // Check for full matches
      if (knownApplianceTypes.some((type) => type.includes(query))) {
        return true;
      }

      return false;
    },

    getMatchingAppliances(property) {
      const query = this.searchQuery.toLowerCase().trim();
      if (!property.appliances) return [];

      return property.appliances.filter(
        (appliance) =>
          (appliance.applianceType &&
            appliance.applianceType.toLowerCase().includes(query)) ||
          (appliance.applianceType === 'Other' &&
            appliance.customType &&
            appliance.customType.toLowerCase().includes(query)),
      );
    },

    getApplianceTypeName(appliance) {
      return appliance.applianceType === 'Other'
        ? appliance.customType
        : appliance.applianceType;
    },

    // Format the Gas or Electric field for display
    formatGasOrElectric(value) {
      if (value === 1) return 'Gas';
      if (value === 2) return 'Electric';
      return 'Not Applicable';
    },

    // This method updates the property data in the properties array with the newly created appliance
    async updatePropertyWithNewAppliance(
      propertyId,
      applianceId,
      applianceData,
    ) {
      try {
        // First find the property in the properties array
        const propertyIndex = this.properties.findIndex(
          (p) => p.id === propertyId,
        );

        if (propertyIndex !== -1) {
          // Create a full appliance object from our data
          const newAppliance = {
            ...applianceData,
            id: applianceId,
            photoCount: 0,
            noteCount: 0,
            propertyId: propertyId,
          };

          // If the property doesn't have an appliances array yet, create one
          if (!this.properties[propertyIndex].appliances) {
            this.properties[propertyIndex].appliances = [];
          }

          // Add the new appliance to the property's appliances array
          this.properties[propertyIndex].appliances.push(newAppliance);

          // Increment the appliance count on the property
          this.properties[propertyIndex].applianceCount += 1;

          console.log(
            'Property data updated in search index with new appliance',
          );

          // If we're using a search term and should re-filter based on new data
          if (this.searchQuery) {
            this.filterProperties();
          }
        }
      } catch (error) {
        console.error('Error updating property with new appliance:', error);
      }
    },

    // Document linking methods
    async toggleDocumentLink() {
      if (!this.selectedAppliance) return;

      try {
        // If there's already a document linked, unlink it
        if (this.selectedAppliance.docId) {
          await axios.put(
            `/api/appliances/${this.selectedAppliance.id}/unlink-document`,
          );
          this.selectedAppliance.docId = null;
        }
        // Otherwise, get the selected document from localStorage and link it
        else {
          const selectedDocument = localStorage.getItem('selectedDocument');
          if (selectedDocument) {
            const docId = parseInt(selectedDocument);
            if (!isNaN(docId)) {
              const response = await axios.put(
                `/api/appliances/${this.selectedAppliance.id}/link-document`,
                { docId },
              );

              if (response.status === 200) {
                this.selectedAppliance.docId = docId;
              }
            } else {
              alert('Invalid document ID in localStorage');
            }
          } else {
            alert('Please select a document first from the Documents page');
          }
        }
      } catch (error) {
        console.error('Error toggling document link:', error);
        alert(
          'Error linking document: ' + (error.message || 'Please try again.'),
        );
      }
    },

    async toggleNoteDocumentLink() {
      try {
        // If there's already a document linked, unlink it
        if (this.noteDocId) {
          this.noteDocId = null;
        }
        // Otherwise, get the selected document from localStorage and link it
        else {
          const selectedDocument = localStorage.getItem('selectedDocument');
          if (selectedDocument) {
            const docId = parseInt(selectedDocument);
            if (!isNaN(docId)) {
              this.noteDocId = docId;
            } else {
              alert('Invalid document ID in localStorage');
            }
          } else {
            alert('Please select a document first from the Documents page');
          }
        }
      } catch (error) {
        console.error('Error toggling note document link:', error);
        alert(
          'Error linking document: ' + (error.message || 'Please try again.'),
        );
      }
    },

    async toggleEditedNoteDocumentLink() {
      try {
        // If there's already a document linked, unlink it
        if (this.editedNoteDocId) {
          this.editedNoteDocId = null;
        }
        // Otherwise, get the selected document from localStorage and link it
        else {
          const selectedDocument = localStorage.getItem('selectedDocument');
          if (selectedDocument) {
            const docId = parseInt(selectedDocument);
            if (!isNaN(docId)) {
              this.editedNoteDocId = docId;
            } else {
              alert('Invalid document ID in localStorage');
            }
          } else {
            alert('Please select a document first from the Documents page');
          }
        }
      } catch (error) {
        console.error('Error toggling edited note document link:', error);
        alert(
          'Error linking document: ' + (error.message || 'Please try again.'),
        );
      }
    },

    viewDocument(docId) {
      if (!docId) return;

      // Check current route to determine which path to use
      const currentRoute = this.$route.path;
      const isDocumentsRoute = currentRoute.includes('/documents');

      // Create the URL based on the current route
      const documentUrl = isDocumentsRoute
        ? `/documents?search=:${docId}`
        : `/mdocs?search=:${docId}`;

      // Open in new tab/window
      window.open(documentUrl, '_blank');
    },

    viewNoteDocument(docId) {
      if (!docId) return;

      // Check current route to determine which path to use
      const currentRoute = this.$route.path;
      const isDocumentsRoute = currentRoute.includes('/documents');

      // Create the URL based on the current route
      const documentUrl = isDocumentsRoute
        ? `/documents?search=:${docId}`
        : `/mdocs?search=:${docId}`;

      // Open in new tab/window
      window.open(documentUrl, '_blank');
    },

    hasDimensionData(appliance) {
      return (
        appliance.width !== null ||
        appliance.height !== null ||
        appliance.depth !== null
      );
    },

    formatDimensions(width, height, depth) {
      const formattedWidth = width !== null ? width : '__';
      const formattedHeight = height !== null ? height : '__';
      const formattedDepth = depth !== null ? depth : '__';
      return `${formattedWidth}x${formattedHeight}x${formattedDepth}`;
    },

    handleSearchFocus() {
      // If we're viewing a property's appliances, go back to search mode
      if (this.selectedProperty) {
        this.selectedProperty = null;
        this.appliances = [];
      }

      // Clear search results
      this.filteredProperties = [];
    },
  },
};
</script>

<style scoped>
.content-wrapper {
  width: 100%;
  /* margin-left: 11px; */
  display: flex;
  flex-direction: column;
  max-width: 420px;
  /* margin: 4px; */
  /* box-shadow: 7px 5px 13px 2px rgba(0, 0, 0, 0.25); */
  background: #f3f3f3;
  /* padding: 8px; */
  /* border-radius: 5px; */
  height: 100%;
}

.title-bar {
  background: #6e5875;
  color: #fff;
  font-size: 1.4em;
  padding: 8px;
  display: flex;
  align-items: center;
  margin-bottom: 0px; /* Changed to 0px to accommodate progress bar */
}

/* Appliance coverage bar styles */
.appliance-coverage-bar {
  background: #f0f0f0;
  padding: 5px 8px;
  border-bottom: 1px solid #ddd;
}

.coverage-label {
  font-size: 0.8em;
  color: #666;
  margin-bottom: 2px;
  text-align: center;
}

.progress-bar {
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 5px;
}

.progress-bar-fill {
  height: 100%;
  background: #a080a8;
  border-radius: 4px;
  transition: width 0.5s ease-in-out;
}

/* In Progress Section Styles */
.in-progress-section {
  background: #f8f9fa;
  border-bottom: 1px solid #ddd;
}

.in-progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  background: #e9ecef;
  border-bottom: 1px solid #ddd;
}

.in-progress-header:hover {
  background: #dee2e6;
}

.in-progress-header h4 {
  margin: 0;
  font-size: 0.9em;
  color: #495057;
  display: flex;
  align-items: center;
}

.in-progress-header h4 i {
  margin-right: 8px;
  color: #6c757d;
}

.in-progress-count {
  background: #a080a8;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75em;
  font-weight: bold;
  margin-left: 8px;
  min-width: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.in-progress-content {
  /* Height and scrolling controlled by .in-progress-list */
}

.no-requests {
  padding: 12px;
  text-align: center;
  color: #6c757d;
  font-style: italic;
  font-size: 0.9em;
}

.in-progress-list {
  padding: 8px;
  max-height: 300px;
  overflow-y: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

/* Hide scrollbar for WebKit browsers (Chrome, Safari, Edge) */
.in-progress-list::-webkit-scrollbar {
  display: none;
}

.in-progress-item {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  margin-bottom: 8px;
  padding: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;
}

.in-progress-item:last-child {
  margin-bottom: 0;
}

.in-progress-note-icon {
  position: absolute;
  bottom: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #b0b0b0;
  font-size: 0.9em;
  transition: color 0.2s ease;
}

.in-progress-note-icon:hover {
  color: #a080a8;
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.appliance-info {
  flex: 1;
  margin-right: 25px;
}

.appliance-info strong {
  color: #495057;
}

.request-type {
  color: #6c757d;
  font-size: 0.85em;
  margin-left: 4px;
}

.mark-received-slider {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  padding: 2px;
}

.slider-track {
  width: 32px;
  height: 16px;
  background-color: #a080a8;
  border-radius: 16px;
  position: relative;
  transition: all 0.3s ease;
}

.slider-thumb {
  width: 12px;
  height: 12px;
  background-color: white;
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: 2px;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.mark-received-slider:hover .slider-track {
  background-color: #8f6e8f;
}

.mark-received-slider:hover .slider-thumb {
  transform: translateX(16px);
}

.slider-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.completed-label {
  font-size: 0.65em;
  color: #6c757d;
  text-align: center;
  font-style: italic;
}

.item-details {
  font-size: 0.8em;
  color: #6c757d;
}

.property-name {
  font-weight: 500;
  color: #495057;
  margin-bottom: 2px;
}

.request-info {
  margin-bottom: 2px;
}

.requested-by {
  color: #6c757d;
}

.most-recent-note {
  color: #6c757d;
  font-size: 0.75em;
  font-style: italic;
  margin-top: 4px;
  line-height: 1.2;
}

.content {
  position: relative;
  padding: 20px;
  height: calc(100% - 250px);
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.content::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.back-button {
  width: 30px;
  height: 30px;
  background: #907793;
  border-radius: 18px;
  border: 1px solid #e1dede;
  font-weight: normal;
  font-size: 0.7em;
  color: #eee;
}

.home {
  height: 30px;
  width: 30px;
  font-size: 0.6em;
  border-radius: 15px;
  color: #fff;
  background: #907793;
}

/* Adding a container for the buttons with gap */
.title-bar span {
  flex: 1;
  text-align: center;
}

.title-bar button {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Group the buttons together with a gap */
.title-bar button + button {
  margin-left: 5px;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 10px;
}

.search-bar input {
  flex: 1;
  font-size: 16pt !important;
}

ul {
  margin-top: 10px;
  list-style-type: none;
  margin: 0;
  padding: 0;
}

li {
  cursor: pointer;
  padding: 10px;
  border-radius: 5px;
  border: 1pt solid #ddd;
  margin-bottom: 8px;
  background: #ddd;
  width: 92%;
  display: flex;
  justify-content: space-between;
}

.badge {
  background: #a080a8;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8em;
}

.add-button {
  background: #a080a8;
  color: white;
  border: none;
  padding: 5px 7px;
  border-radius: 3px;
  margin: 10px 0;
  cursor: pointer;
  font-size: 0.9em;
}

.no-appliances,
.no-photos,
.no-notes,
.no-results {
  color: #666;
  font-style: italic;
  margin: 15px 0;
}

.appliance-item {
  flex: 1;
}

.appliance-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
}

.appliance-name {
  font-weight: bold;
}

.appliance-serial {
  font-size: 0.8em;
  color: #666;
}

.appliance-type {
  font-size: 0.8em;
  color: #666;
  margin-top: 5px;
}

.type-attribute {
  font-size: 0.8em;
  color: #666;
  margin-left: 5px;
}

.dimensions {
  font-size: 0.8em;
  color: #666;
  margin-left: 5px;
}

.appliance-date {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.8em;
  margin-top: 5px;
}

.date-pair {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.date-pair > .form-group {
  flex: 1;
}

.date-field-error {
  margin: 4px 0 0;
  color: #a94442;
  font-size: 0.85em;
}

.age-display {
  color: #777;
  font-style: italic;
}

.appliance-condition {
  display: flex;
  align-items: center;
  font-size: 0.8em;
  margin: 3px 0;
}

.condition-rating,
.condition-rating-edit {
  display: flex;
  margin-left: 5px;
}

.rating-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #bebebe;
  margin-right: 2px;
  border: 1px solid #888;
}

.rating-dot.filled {
  background: #907793;
  border: 1px solid #aaa;
}

.rating-dot.clickable {
  cursor: pointer;
}

.icon-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  color: #a080a8;
}

.icon-button.delete {
  color: #f44336;
}

.appliance-actions {
  display: flex;
  flex-direction: column;
}

.details-form-container {
  overflow-y: auto;
  padding-right: 10px;
  margin-top: 10px;
  border-top: 1px solid #eaeaea;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  max-height: 98vh;
}

.details-form-container::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.details-form {
  display: flex;
  flex-direction: column;
  margin-top: 10px;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
}

.form-group label {
  font-weight: bold;
  margin-bottom: 4px;
}

input,
textarea,
button {
  padding: 6px;
  border-radius: 4px;
  border: 1pt solid #d4d4d4;
  outline: none;
  font-size: 1em;
}

input,
textarea,
.readonly {
}

.readonly {
  background-color: lightgray;
  min-height: 18px; /* Add fixed height to all readonly fields */
  padding: 6px;
}

.clear-search {
  background: #a080a8;
  color: white;
  border: none;
  padding: 5px 7px;
  border-radius: 3px;
  margin-left: 10px;
  cursor: pointer;
  font-size: 0.9em;
}

/* Add media query for larger screens */
@media (min-width: 400px) {
  input,
  textarea,
  .readonly {
  }
}

.section-header {
  margin-top: 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ddd;
  padding-bottom: 5px;
}

.section-header h4 {
  margin-top: 4px;
  margin-bottom: 4px;
}

.add-small-button {
  background: #a080a8;
  color: white;
  border: none;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  margin-right: 28px;
  font-size: 0.8em;
}

.photo-carousel {
  overflow-x: hidden;
  margin: 10px 0;
  display: flex;
  align-items: center;
  position: relative;
}

.carousel-inner {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  padding: 5px 0;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  width: 100%;
}

.carousel-inner::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.photo-item {
  position: relative;
  width: 120px;
  flex-shrink: 0;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.photo-item:hover {
  transform: translateY(-3px);
}

.photo-item img {
  width: 100%;
  height: 90px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ddd;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.photo-caption {
  font-size: 0.7em;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 4px;
  color: #555;
}

.photo-appliance {
  font-size: 0.7em;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 4px;
  color: #555;
}

.photo-address {
  font-size: 0.7em;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 4px;
  color: #555;
}

.delete-photo-button {
  position: absolute;
  top: 2px;
  right: 2px;
  background: #a080a8;
  color: white;
  border: none;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7em;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  z-index: 5;
}

.carousel-arrow {
  background: #a080a8;
  color: white;
  border: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: absolute;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  opacity: 0.9;
  transition: opacity 0.2s ease;
  font-size: 0.7em;
}

.carousel-arrow:hover {
  opacity: 1;
}

.carousel-arrow.left {
  left: 0px;
}

.carousel-arrow.right {
  right: 0px;
}

.photo-viewer {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.photo-viewer-content {
  max-width: 95%;
  max-height: 95%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.photo-viewer-content img {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
}

.photo-viewer-caption {
  color: white;
  text-align: center;
  margin-top: 15px;
  font-size: 0.9em;
  background: rgba(0, 0, 0, 0.5);
  padding: 5px 10px;
  border-radius: 4px;
  max-width: 90%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative; /* Add position relative */
}

.edit-caption-button {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  cursor: pointer;
  font-size: 0.8em;
  margin-left: 10px;
  padding: 3px;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.9;
  transition: all 0.2s ease;
}

.edit-caption-button:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.4);
  transform: scale(1.1);
}

.photo-viewer-close {
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  font-size: 0.8em;
  margin-top: 20px;
}

.add-note {
  margin: 10px 0;
}

.dimensions-container {
  margin-bottom: 15px;
}

.dimensions-row {
  display: flex;
  gap: 10px;
  justify-content: space-between;
  width: 75%;
}

.dimension-input {
  display: flex;
  flex-direction: column;
  flex: 0 0 15%;
  width: 15%;
}

.dimension-input label {
  font-size: 0.8em;
  margin-bottom: 2px;
  color: #666;
}

.dimension-input input {
  width: 100% !important;
}

.select-wrapper {
}

.save-note-button {
  background: #a080a8;
  color: white;
  border: none;
  margin-top: 5px;
  cursor: pointer;
  padding: 4px 7px;
  font-size: 0.9em;
}

.link-document-button {
  background: #a080a8;
  color: white;
  border: none;
  margin-top: 5px;
  cursor: pointer;
  padding: 4px 7px;
  font-size: 0.9em;
}

.link-document-button.active {
  background: #756182; /* Darker color when document is linked */
}

.notes-list {
  margin: 10px 0;
}

.note-item {
  background: #fff;
  border-radius: 4px;
  padding: 8px;
  margin-bottom: 8px;
  border: 1px solid #ddd;
}

.note-content {
  flex: 1;
  word-break: break-word;
}

.note-meta {
  font-size: 0.7em;
  color: #666;
  margin-top: 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.note-meta-info {
  display: flex;
  align-items: center;
  gap: 5px;
}

.note-document-icon {
  color: #4caf50;
  cursor: pointer;
  margin-left: 5px;
  font-size: 1.3em; /* Increased from 1.1em */
}

.edit-note-button,
.delete-note-button {
  background: none;
  border: none;
  color: #a080a8; /* Both pencil and trash icons now use the same color */
  cursor: pointer;
  padding: 0;
  font-size: 1.2em; /* Added to make these icons larger */
}

.modified-date {
  font-size: 0.8em;
  margin-top: 20px;
}

.edit-button {
  width: 24px;
  height: 24px;
  background: #a898b7;
  color: #fff;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 0;
  font-size: 0.8em;
}

.delete-button {
  width: 24px;
  height: 24px;
  background: #a898b7;
  color: #fff;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 0;
  font-size: 0.8em;
}

.link-button {
  width: 24px;
  height: 24px;
  background: #a898b7;
  color: #fff;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 0;
  font-size: 0.8em;
}

.link-button.active {
  background: #756182; /* Darker color when document is linked */
}

.document-indicator {
  color: #4caf50; /* Green color for document indicator */
}

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  max-width: 420px;
}

.dialog-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 400px;
  overflow: hidden;
}

.appliance-dialog {
  max-width: 500px;
}

.dialog-header {
  background: #a080a8;
  color: white;
  padding: 10px 15px;
  font-size: 1.2em;
}

.dialog-body {
  padding: 15px;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  padding: 10px 15px;
  background: #f5f5f5;
}

.dialog-actions button {
  margin-left: 10px;
  padding: 5px 9px;
  cursor: pointer;
  font-size: 0.9em;
}

.confirm-button {
  background: #c9a0d3;
  color: white;
  border: none;
  padding: 5px 9px;
  font-size: 0.9em;
}

.caption-input {
  margin-top: 10px;
  width: 100%;
}

.appliance-header {
  margin-bottom: 5px;
  position: sticky;
  top: 0;
  background: #f3f3f3;
  padding-bottom: 5px;
  z-index: 10;
}

.appliance-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.appliance-header h3 {
  margin-bottom: 0;
  margin-top: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 75%; /* Ensure there's space for the buttons */
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.action-buttons button {
  width: 32px;
  height: 32px;
  background: #a898b7;
  color: #fff;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 0;
  font-size: 0.8em;
}

.appliance-type-selector {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.appliance-type-selector select {
  padding: 6px;
  border-radius: 4px;
  border: 1pt solid #d4d4d4;
  background-color: white;
  font-size: 12pt;
}

.appliance-type-display {
  padding: 6px;
  border-radius: 4px;
  min-height: 18px; /* Add fixed height to prevent collapse */
}

.custom-type-input {
  margin-top: 5px;
}

:deep(.highlight) {
  background-color: #a898b7;
}

.example {
  font-size: 0.8em;
  margin-top: 10px;
  margin-bottom: 10px;
}

.example-indent {
  font-size: 0.8em;
  margin-top: -10px;
  margin-bottom: 10px;
  margin-left: 40px;
}

.match-label {
  margin-top: 20px;
  margin-bottom: 10px;
  color: #907793;
}

.upload-instructions {
  font-size: 0.9em;
  margin-bottom: 10px;
  color: #666;
}

.preview-container {
  margin-top: 10px;
  text-align: center;
}

.photo-preview {
  max-width: 100%;
  max-height: 200px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.multi-preview-container {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
}

.multi-preview-item {
  width: 80px;
  height: 80px;
}

.photo-preview-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.photo-count {
  margin-top: 10px;
  font-size: 0.9em;
  color: #666;
}

.appliance-indicators {
  display: flex;
  gap: 5px; /* Changed from 8px to 5px as requested */
  margin-left: auto;
  justify-content: flex-end; /* Align icons to the right edge */
}

.appliance-indicators i {
  font-size: 1.1em;
  color: #a080a8;
  background-color: rgba(255, 255, 255, 0.7);
  padding: 4px;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.document-indicator {
  color: #4caf50; /* Green color for document indicator */
}

.confirm-dialog-button {
  min-width: 60px;
  padding: 5px 9px;
  border-radius: 3px;
  font-size: 0.9em;
  cursor: pointer;
  flex: 1;
  margin: 0 5px;
}

.cancel-button {
  background: #f5f5f5;
  color: #666;
  border: 1px solid #ddd;
}

.delete-confirm-button {
  background: #a080a8;
  color: white;
  border: none;
}

.type-matches {
  margin-top: 5px;
}

.type-match {
  font-size: 0.8em;
  color: #666;
  display: flex;
  align-items: center;
  gap: 5px;
}

.type-match i {
  color: #a080a8;
  font-size: 0.8em;
}

.property-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.loading-indicator {
  color: #666;
  font-style: italic;
  margin: 15px 0;
  display: flex;
  align-items: center;
  gap: 5px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
  text-align: center;
  height: 410px; /* Make it take up half the viewport height */
}

.empty-icon {
  margin-top: 8px;
  font-size: 4em;
  color: #d9d9d9;
}

.empty-state div {
  color: #777777; /* Medium grey color for the text */
}

/* Content overlay dialog */
.content-overlay-dialog {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  max-width: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(243, 243, 243, 0.95);
}

.content-blurred {
  filter: blur(2px);
  pointer-events: none;
}

/* Adjust dialog content for overlay display */
.content-overlay-dialog .dialog-content {
  font-size: 0.9em;
  width: 89%;
  display: flex;
  flex-direction: column;
  max-height: 85%;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  border: 1px solid #cacaca;
}

.content-overlay-dialog .dialog-header {
  position: sticky;
  top: 0;
  z-index: 2;
}

.content-overlay-dialog .dialog-body {
  flex: 1;
  overflow-y: auto;
  padding: 15px;
  max-height: calc(100vh - 250px);
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.content-overlay-dialog .dialog-body::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.content-overlay-dialog .dialog-actions {
  position: sticky;
  bottom: 0;
  z-index: 2;
  background: #a080a8;
  border-top: 1px solid #ddd;
}

/* Remove the old inline dialog styles */
.inline-dialog-container {
  display: none;
}

/* Details view with fixed header */
.details-view {
  display: flex;
  flex-direction: column;
  height: 84%;
}

.fixed-header {
  position: sticky;
  top: 0;
  background: #f3f3f3;
  z-index: 20;
  padding-bottom: 10px;
  border-bottom: 1px solid #ddd;
  margin-bottom: 10px;
}

.back-button-2 {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 10px;
  color: #907793;
  font-weight: bold;
  cursor: pointer;
}

.note-actions {
  display: flex;
  gap: 4px; /* Keep the 4px gap between buttons as requested in todo.txt */
  margin-top: 5px;
  align-items: center; /* Added to match action-buttons */
  justify-content: flex-end; /* Added to match action-buttons */
  margin-right: 5px;
}

.note-actions-small {
  display: flex;
  gap: 4px; /* Keep the 4px gap between buttons */
  align-items: center; /* Added to match action-buttons */
  justify-content: flex-end; /* Added to match action-buttons */
}

.edit-note-button {
  background: none;
  border: none;
  color: #a080a8;
  cursor: pointer;
  padding: 0;
}

.cancel-note-button {
  background: #f0f0f0;
  color: #666;
  border: none;
  margin-top: 5px;
  cursor: pointer;
  padding: 4px 7px;
  font-size: 0.9em;
}

.note-edit-area {
  width: 100%;
}

.photo-search {
  font-size: 16pt;
}

.upload-options {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  gap: 10px; /* Add gap between buttons */
}

.upload-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  width: 45%; /* Make each option take 45% of the container width */
}

.hidden-input {
  display: none;
}

.option-button {
  background: #a080a8;
  color: white;
  border-radius: 4px;
  padding: 10px;
  text-align: center;
  font-size: 0.9em;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 100%; /* Take full width of parent (upload-option) */
}

/* Recent Photos Section Styles */
.recent-photos-section {
  margin-top: 30px;
  width: 100%;
}

.section-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 15px;
}

.section-title-row h3 {
  text-align: center;
  color: #907793;
  flex: 1;
  margin: 0;
}

.refresh-button {
  background: #a080a8;
  color: white;
  border: none;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  font-size: 0.8em;
  margin-left: auto;
}

.photo-carousel {
  position: relative;
  margin: 10px 0;
  width: 100%;
}

.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 5;
  background: rgba(160, 128, 168, 0.7);
  color: white;
  border: none;
  width: 25px;
  height: 25px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.carousel-arrow.left {
  left: 0;
}

.carousel-arrow.right {
  right: 0;
}

.carousel-inner {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding: 10px 25px; /* Add padding to prevent items being hidden behind arrows */
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Hide scrollbars in Firefox */
  -ms-overflow-style: none; /* Hide scrollbars in IE/Edge */
}

.carousel-inner::-webkit-scrollbar {
  display: none; /* Hide scrollbars in Chrome/Safari/Opera */
}

.photo-item {
  min-width: 150px;
  flex-shrink: 0;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  background: white;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.photo-item:hover {
  transform: translateY(-5px);
}

.photo-item img {
  width: 100%;
  height: 120px;
  object-fit: cover;
}

.photo-caption,
.photo-appliance,
.photo-address {
  padding: 5px;
  font-size: 0.8em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
}

.photo-caption {
  color: #666;
  border-top: 1px solid #eee;
  padding-top: 5px;
}

.photo-appliance {
  font-weight: bold;
  color: #555;
  padding-bottom: 0;
  margin-bottom: 0;
}

.photo-address {
  color: #888;
  font-size: 0.75em;
  padding-top: 0;
  margin-top: 0;
}

.loading-photos {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 150px;
  width: 100%;
  color: #907793;
  font-style: italic;
}

.no-recent-photos {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 150px;
  width: 100%;
  color: #888;
  font-style: italic;
  background: rgba(240, 240, 240, 0.5);
  border-radius: 8px;
}

/* Carousel controls styles */
.carousel-controls {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.carousel-dots {
  display: flex;
  gap: 5px;
}

.carousel-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ddd;
  cursor: pointer;
}

.carousel-dot.active {
  background: #907793;
}

/* Appliances list wrapper */
.appliances-list-wrapper {
  max-height: 62vh; /* Adjust height as needed */
  overflow-y: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.appliances-list-wrapper::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

@media (min-width: 600px) {
  .empty-state {
    height: 410px;
  }

  .appliances-list-wrapper {
    max-height: 52vh; /* Adjust height as needed */
  }
  .details-form-container {
    max-height: 86vh;
  }
}
.full-size-photo {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
  transform-origin: center;
  transition: transform 0.1s ease;
  will-change: transform;
  touch-action: none; /* Prevents browser's default touch actions */
  user-select: none; /* Prevents text selection during zoom */
}
.request-type-select {
  padding: 6px;
  border-radius: 4px;
  border: 1pt solid #d4d4d4;
  background-color: white;
  font-size: 10pt;
}

/* In-progress note dialog styles */
.note-textarea {
  width: 100%;
  min-height: 100px;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  margin-top: 10px;
}

.appliance-info {
  padding: 8px;
  background-color: #f5f5f5;
  border-radius: 4px;
  margin-bottom: 10px;
  font-size: 14px;
}

.appliance-info .property-name {
  color: #666;
  font-size: 12px;
}
</style>
